import { apiBaseUrl } from "../auth-config";
import type { LocalProfile } from "./auth-types";

export interface SessionResponse {
  accessToken: string;
  tokenType: string;
  user: {
    googleSub: string;
    email: string;
    role: string;
    enrollmentSummary?: string;
  };
}

/** Response from POST /api/auth/oauth/complete (server-side Google code exchange + app JWT). */
export interface OAuthCompleteResponse extends SessionResponse {
  googleIdToken?: string;
  googleAccessToken?: string;
}

function url(path: string): string {
  const base = apiBaseUrl.replace(/\/$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

const GET_RETRY_MAX_ATTEMPTS = 3;
const GET_RETRYABLE_STATUSES = new Set([429, 500, 502, 503, 504]);
const GET_RETRYABLE_DB_ERROR_RE =
  /(database error|database unavailable|database system is starting up|resum|auto-paused|please wait a few seconds)/i;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getErrorMessage(data: unknown): string {
  if (!data || typeof data !== "object" || !("error" in data)) return "";
  return typeof (data as { error?: unknown }).error === "string" ? (data as { error?: string }).error ?? "" : "";
}

function shouldRetryTransientGet(status: number, errorMessage: string): boolean {
  return GET_RETRYABLE_STATUSES.has(status) || GET_RETRYABLE_DB_ERROR_RE.test(errorMessage);
}

export class ApiClientError extends Error {
  readonly code?: string;
  readonly status?: number;

  constructor(message: string, opts?: { code?: string; status?: number }) {
    super(message);
    this.name = "ApiClientError";
    this.code = opts?.code;
    this.status = opts?.status;
  }
}

async function getJsonWithTransientRetries<T>(
  path: string,
  bearer: string
): Promise<{ res: Response; data: T & { error?: string } }> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= GET_RETRY_MAX_ATTEMPTS; attempt++) {
    try {
      const res = await fetch(url(path), {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      const data = (await res.json().catch(() => ({}))) as T & { error?: string };
      if (res.ok || attempt >= GET_RETRY_MAX_ATTEMPTS || !shouldRetryTransientGet(res.status, getErrorMessage(data))) {
        return { res, data };
      }
    } catch (error) {
      lastError = error;
      if (attempt >= GET_RETRY_MAX_ATTEMPTS) {
        throw error;
      }
    }
    await sleep(1_000 * 2 ** (attempt - 1));
  }
  throw lastError instanceof Error ? lastError : new Error("GET request failed");
}

export async function postOAuthComplete(body: {
  code: string;
  codeVerifier: string;
  redirectUri: string;
}): Promise<OAuthCompleteResponse> {
  const res = await fetch(url("/api/auth/oauth/complete"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as OAuthCompleteResponse & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `OAuth completion failed (${res.status})`);
  }
  if (!data.accessToken) {
    throw new Error("OAuth completion response missing accessToken");
  }
  return data;
}

export async function postAuthSession(idToken: string): Promise<SessionResponse> {
  const res = await fetch(url("/api/auth/session"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ idToken }),
  });
  const data = (await res.json().catch(() => ({}))) as SessionResponse & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `Session exchange failed (${res.status})`);
  }
  if (!data.accessToken) {
    throw new Error("Session response missing accessToken");
  }
  return data as SessionResponse;
}

export interface DemoMarkDoneResponse {
  submissionId: string;
  outcome: "passed" | "failed";
  summary: string;
  checks: { id: string; label: string; passed: boolean }[];
  validationEngine: string;
}

export async function postStudentDemoMarkDone(
  bearer: string,
  mainPy: string
): Promise<DemoMarkDoneResponse> {
  const res = await fetch(url("/api/student/demo/mark-done"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify({ mainPy }),
  });
  const data = (await res.json().catch(() => ({}))) as DemoMarkDoneResponse & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `Check My Work failed (${res.status})`);
  }
  if (!data.submissionId || !data.outcome) {
    throw new Error("Check My Work response incomplete");
  }
  return data as DemoMarkDoneResponse;
}

export type CommunityPublishStatus = "private" | "pending_review" | "approved" | "rejected";

export interface TeacherCourseRecord {
  id: string;
  title: string;
  description: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  status: "active" | "archived";
  activeCohorts?: number;
  communityPublishStatus?: CommunityPublishStatus;
  heroImageKey?: string | null;
  heroImageUrl?: string | null;
  accentColor?: string | null;
  version?: number;
  sourceCommunityCourseId?: string | null;
}

export type LessonWorkspaceMode = "simple" | "advanced";

export interface TeacherLessonRecord {
  id: string;
  courseId?: string;
  ownerTeacherId?: string;
  title: string;
  slug: string | null;
  position?: number;
  starterFilesJson: unknown;
  packagePolicyJson?: unknown;
  createdAt: string;
  updatedAt: string;
  creationPrompt?: string | null;
  description?: string | null;
  iconName?: string | null;
  iconBgColor?: string | null;
  iconFgColor?: string | null;
  workspaceMode?: LessonWorkspaceMode;
  deletedAt?: string | null;
  status?: "active" | "deleted";
  usedInTemplates?: number;
  activeCohorts?: number;
  studentsAssigned?: number;
  studentsFinished?: number;
  communityPublishStatus?: CommunityPublishStatus;
  isDraft?: boolean;
  version?: number;
  sourceCommunityLessonId?: string | null;
  inspirationDetached?: boolean;
  updateAvailable?: boolean;
  standardsCount?: number;
  glossaryCount?: number;
  /** Present when GET /api/lessons?highlightStandardId=… was used. */
  coversHighlightStandard?: boolean;
}

export interface LessonPresentationFields {
  creationPrompt?: string | null;
  description?: string | null;
  iconName?: string | null;
  iconBgColor?: string | null;
  iconFgColor?: string | null;
  workspaceMode?: LessonWorkspaceMode;
}

export interface TeacherTourStepRecord {
  id: string;
  ordinal: number;
  title: string;
  body: string[];
  referenceCode: string | null;
}

export type TeacherLessonTestVisibility = "hidden" | "summary" | "detailed";

export interface TeacherLessonTestRecord {
  id: string;
  lessonId: string;
  ordinal: number;
  name: string;
  isRequired: boolean;
  definitionJson: unknown;
  visibility: TeacherLessonTestVisibility;
  description: string | null;
  testCode: string;
}

export interface LessonAiTourStepDraft {
  title: string;
  body: string[];
  referenceCode?: string | null;
}

export interface LessonAiTestDraft {
  name: string;
  description?: string;
  testCode: string;
  isRequired?: boolean;
  visibility?: TeacherLessonTestVisibility;
}

export interface LessonAiDraft {
  title: string;
  slug?: string;
  description?: string;
  iconName?: string;
  iconBgColor?: string;
  iconFgColor?: string;
  workspaceMode?: LessonWorkspaceMode;
  /** AI-only hint for teachers when advanced mode is suggested; not saved on the lesson. */
  advancedModeRationale?: string;
  mainPy: string;
  starterFiles?: { path: string; content: string }[];
  tourSteps: LessonAiTourStepDraft[];
  tests: LessonAiTestDraft[];
  /** Standards/glossary the AI determined this lesson aligns to (codes / terms). */
  alignedStandardCodes?: string[];
  alignedGlossaryTerms?: string[];
}

export interface LessonAiStandardsRequest {
  standardSetId?: string;
  selectedStandardIds?: string[];
  selectedGlossaryIds?: string[];
}

export interface CourseAiDraft {
  title: string;
  description: string;
  lessonIds: string[];
  rationale?: string;
}

type CourseAiJobPollResponse = {
  jobId: string;
  status: "pending" | "running" | "done" | "failed";
  kind?: string;
  draft?: CourseAiDraft;
  error?: string;
};

async function pollCourseAiJob(
  bearer: string,
  jobId: string
): Promise<{ draft: CourseAiDraft }> {
  const maxAttempts = 90;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const res = await fetch(url(`/api/courses/ai/jobs/${encodeURIComponent(jobId)}`), {
      headers: { Authorization: `Bearer ${bearer}` },
    });
    const data = (await res.json().catch(() => ({}))) as CourseAiJobPollResponse & {
      error?: string;
      code?: string;
    };
    if (!res.ok) {
      throw new ApiClientError(data.error ?? `AI job poll failed (${res.status})`, {
        code: data.code,
        status: res.status,
      });
    }
    if (data.status === "done") {
      if (!data.draft) throw new Error("AI job completed without a draft");
      return { draft: data.draft };
    }
    if (data.status === "failed") {
      throw new ApiClientError(data.error ?? "Course template generation failed", { status: 500 });
    }
    await sleep(attempt < 5 ? 1500 : 2500);
  }
  throw new ApiClientError("Course template generation is taking longer than expected. Try again in a moment.", {
    status: 504,
  });
}

async function postCourseAiAsync(
  bearer: string,
  path: "/api/courses/ai/generate" | "/api/courses/ai/refine",
  body: Record<string, unknown>,
  label: "generate" | "refine"
): Promise<{ draft: CourseAiDraft }> {
  const res = await fetch(url(path), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    jobId?: string;
    status?: string;
    draft?: CourseAiDraft;
    error?: string;
    code?: string;
  };
  if (res.status === 202 && data.jobId) {
    return pollCourseAiJob(bearer, data.jobId);
  }
  if (res.ok && data.draft) {
    return { draft: data.draft };
  }
  if (!res.ok) {
    throw new ApiClientError(data.error ?? `AI ${label} failed (${res.status})`, {
      code: data.code,
      status: res.status,
    });
  }
  throw new Error(`AI ${label} response incomplete`);
}

export async function postCourseAiGenerate(
  bearer: string,
  body: { prompt: string; draft?: CourseAiDraft } & LessonAiStandardsRequest
): Promise<{ draft: CourseAiDraft }> {
  return postCourseAiAsync(bearer, "/api/courses/ai/generate", body as unknown as Record<string, unknown>, "generate");
}

export async function postCourseAiRefine(
  bearer: string,
  body: { prompt: string; draft: CourseAiDraft } & LessonAiStandardsRequest
): Promise<{ draft: CourseAiDraft }> {
  return postCourseAiAsync(bearer, "/api/courses/ai/refine", body as unknown as Record<string, unknown>, "refine");
}

type LessonAiJobPollResponse = {
  jobId: string;
  status: "pending" | "running" | "done" | "failed";
  kind?: string;
  draft?: LessonAiDraft;
  error?: string;
};

async function pollLessonAiJob(bearer: string, jobId: string): Promise<{ draft: LessonAiDraft }> {
  const maxAttempts = 90;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const res = await fetch(url(`/api/lessons/ai/jobs/${encodeURIComponent(jobId)}`), {
      headers: { Authorization: `Bearer ${bearer}` },
    });
    const data = (await res.json().catch(() => ({}))) as LessonAiJobPollResponse & {
      error?: string;
      code?: string;
    };
    if (!res.ok) {
      throw new ApiClientError(data.error ?? `AI job poll failed (${res.status})`, {
        code: data.code,
        status: res.status,
      });
    }
    if (data.status === "done") {
      if (!data.draft) throw new Error("AI job completed without a draft");
      return { draft: data.draft };
    }
    if (data.status === "failed") {
      throw new ApiClientError(data.error ?? "Lesson generation failed", { status: 500 });
    }
    await sleep(attempt < 5 ? 1500 : 2500);
  }
  throw new ApiClientError("Lesson generation is taking longer than expected. Try again in a moment.", {
    status: 504,
  });
}

async function postLessonAiAsync(
  bearer: string,
  path: "/api/lessons/ai/generate" | "/api/lessons/ai/refine",
  body: Record<string, unknown>,
  label: "generate" | "refine"
): Promise<{ draft: LessonAiDraft }> {
  const res = await fetch(url(path), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    jobId?: string;
    status?: string;
    draft?: LessonAiDraft;
    error?: string;
    code?: string;
  };
  if (res.status === 202 && data.jobId) {
    return pollLessonAiJob(bearer, data.jobId);
  }
  if (res.ok && data.draft) {
    return { draft: data.draft };
  }
  if (!res.ok) {
    const gatewayTimeout =
      res.status === 503 && !data.error
        ? "Request timed out at the gateway (30s). Deploy the latest API for async lesson generation."
        : undefined;
    throw new ApiClientError(
      data.error ?? gatewayTimeout ?? `AI ${label} failed (${res.status})`,
      { code: data.code, status: res.status }
    );
  }
  throw new Error(`AI ${label} response incomplete`);
}

export async function postLessonAiGenerate(
  bearer: string,
  body: {
    prompt: string;
    draft?: LessonAiDraft;
    forceWorkspaceMode?: LessonWorkspaceMode;
  } & LessonAiStandardsRequest
): Promise<{ draft: LessonAiDraft }> {
  return postLessonAiAsync(bearer, "/api/lessons/ai/generate", body as unknown as Record<string, unknown>, "generate");
}

export async function postLessonAiRefine(
  bearer: string,
  body: {
    prompt: string;
    draft: LessonAiDraft;
    forceWorkspaceMode?: LessonWorkspaceMode;
  } & LessonAiStandardsRequest
): Promise<{ draft: LessonAiDraft }> {
  return postLessonAiAsync(bearer, "/api/lessons/ai/refine", body as unknown as Record<string, unknown>, "refine");
}

export interface CurriculumBundleV1 {
  format: "ccs-curriculum-bundle";
  version: number;
  exportedAt: string;
  lessons: {
    slug: string | null;
    title: string;
    starterFilesJson: unknown;
    packagePolicyJson: unknown;
    tourSteps: LessonAiTourStepDraft[];
    tests: {
      name: string;
      ordinal: number;
      description?: string | null;
      testCode: string;
      isRequired: boolean;
      visibility: string;
    }[];
  }[];
  courses: {
    slug: string;
    title: string;
    description: string | null;
    archived: boolean;
    lessonSlugs: string[];
  }[];
}

export async function getCurriculumExport(
  bearer: string,
  params?: { scope?: "all" | "lessons" | "templates"; courseId?: string }
): Promise<{ bundle: CurriculumBundleV1 }> {
  const qs = new URLSearchParams();
  if (params?.scope) qs.set("scope", params.scope);
  if (params?.courseId) qs.set("courseId", params.courseId);
  const q = qs.toString();
  const { res, data } = await getJsonWithTransientRetries<{ bundle?: CurriculumBundleV1; error?: string }>(
    `/api/curriculum/export${q ? `?${q}` : ""}`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `Export failed (${res.status})`);
  if (!data?.bundle) throw new Error("Export response incomplete");
  return data as { bundle: CurriculumBundleV1 };
}

export async function postCurriculumImport(
  bearer: string,
  body: { bundle: CurriculumBundleV1; mode?: "merge" | "replace-lessons"; confirm: true }
): Promise<{
  imported: { lessonsImported: number; templatesImported: number; lessonIdsBySlug: Record<string, string> };
}> {
  const res = await fetch(url("/api/curriculum/import"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    imported?: { lessonsImported: number; templatesImported: number; lessonIdsBySlug: Record<string, string> };
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Import failed (${res.status})`);
  if (!data.imported) throw new Error("Import response incomplete");
  return data as {
    imported: { lessonsImported: number; templatesImported: number; lessonIdsBySlug: Record<string, string> };
  };
}

export async function getTeacherCourses(
  bearer: string,
  params?: { limit?: number; offset?: number; includeArchived?: boolean }
): Promise<{ courses: TeacherCourseRecord[]; page: { limit: number; offset: number; total: number } }> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  if (params?.includeArchived) qs.set("includeArchived", "true");
  const q = qs.toString();
  const { res, data } = await getJsonWithTransientRetries<{
    courses?: TeacherCourseRecord[];
    page?: { limit: number; offset: number; total: number };
  }>(`/api/courses${q ? `?${q}` : ""}`, bearer);
  if (!res.ok) {
    throw new Error(data?.error ?? `GET /api/courses failed (${res.status})`);
  }
  if (!data?.courses || !data.page) throw new Error("List course templates response incomplete");
  return data as { courses: TeacherCourseRecord[]; page: { limit: number; offset: number; total: number } };
}

export async function postTeacherCourse(
  bearer: string,
  body: { title: string; description?: string; lessonIds?: string[] }
): Promise<{ course: TeacherCourseRecord; lessonIds?: string[] }> {
  const res = await fetch(url("/api/courses"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    course?: TeacherCourseRecord;
    lessonIds?: string[];
    error?: string;
  };
  if (!res.ok) {
    throw new ApiClientError(data.error ?? `POST /api/courses failed (${res.status})`, {
      code: (data as { code?: string }).code,
      status: res.status,
    });
  }
  if (!data.course) throw new Error("Create course template response incomplete");
  return data as { course: TeacherCourseRecord; lessonIds?: string[] };
}

export async function getTeacherCourse(
  bearer: string,
  courseId: string
): Promise<{ course: TeacherCourseRecord; inspirationCount?: number }> {
  const { res, data } = await getJsonWithTransientRetries<{
    course?: TeacherCourseRecord;
    inspirationCount?: number;
    error?: string;
  }>(`/api/courses/${encodeURIComponent(courseId)}`, bearer);
  if (!res.ok) {
    throw new Error(data.error ?? `GET course template failed (${res.status})`);
  }
  if (!data.course) throw new Error("Get course template response incomplete");
  return data as { course: TeacherCourseRecord; inspirationCount?: number };
}

export async function patchTeacherCourse(
  bearer: string,
  courseId: string,
  body: {
    title?: string;
    description?: string;
    heroImageKey?: string | null;
    accentColor?: string | null;
    changelog?: string | null;
  }
): Promise<{ course: TeacherCourseRecord }> {
  const res = await fetch(url(`/api/courses/${encodeURIComponent(courseId)}`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { course?: TeacherCourseRecord; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `PATCH /api/courses/{id} failed (${res.status})`);
  }
  if (!data.course) throw new Error("Patch course template response incomplete");
  return data as { course: TeacherCourseRecord };
}

export async function postTeacherCourseArchive(
  bearer: string,
  courseId: string
): Promise<{ course: TeacherCourseRecord; activeCohorts?: number }> {
  const res = await fetch(url(`/api/courses/${encodeURIComponent(courseId)}/archive`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    course?: TeacherCourseRecord;
    activeCohorts?: number;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `POST course template archive failed (${res.status})`);
  }
  if (!data.course) throw new Error("Archive course template response incomplete");
  return data as { course: TeacherCourseRecord; activeCohorts?: number };
}

export interface TeacherCourseCohortRecord {
  id: string;
  label: string;
  courseId: string;
  status: "active" | "archived";
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  activeJoinCode: string | null;
}

export async function getTeacherCourseCohorts(
  bearer: string,
  courseId: string
): Promise<{ cohorts: TeacherCourseCohortRecord[] }> {
  const { res, data } = await getJsonWithTransientRetries<{ cohorts?: TeacherCourseCohortRecord[] }>(
    `/api/courses/${encodeURIComponent(courseId)}/cohorts`,
    bearer
  );
  if (!res.ok) {
    throw new Error(data?.error ?? `GET course template cohorts failed (${res.status})`);
  }
  if (!data.cohorts) throw new Error("Course template cohorts response incomplete");
  return data as { cohorts: TeacherCourseCohortRecord[] };
}

export async function getLibraryLessons(
  bearer: string,
  params?: {
    limit?: number;
    offset?: number;
    q?: string;
    deleted?: boolean;
    drafts?: boolean;
    highlightStandardId?: string;
  }
): Promise<{ lessons: TeacherLessonRecord[]; page: { limit: number; offset: number; total: number } }> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  if (params?.q?.trim()) qs.set("q", params.q.trim());
  if (params?.deleted) qs.set("deleted", "true");
  if (params?.drafts) qs.set("drafts", "true");
  if (params?.highlightStandardId?.trim()) qs.set("highlightStandardId", params.highlightStandardId.trim());
  const q = qs.toString();
  const { res, data } = await getJsonWithTransientRetries<{
    lessons?: TeacherLessonRecord[];
    page?: { limit: number; offset: number; total: number };
    error?: string;
  }>(`/api/lessons${q ? `?${q}` : ""}`, bearer);
  if (!res.ok) throw new Error(data?.error ?? `GET /api/lessons failed (${res.status})`);
  if (!data?.lessons || !data.page) throw new Error("List library lessons response incomplete");
  return data as { lessons: TeacherLessonRecord[]; page: { limit: number; offset: number; total: number } };
}

export async function postLibraryLesson(
  bearer: string,
  body: {
    title: string;
    slug?: string;
    starterFilesJson?: unknown;
    packagePolicyJson?: unknown;
    isDraft?: boolean;
  } & LessonPresentationFields
): Promise<{ lesson: TeacherLessonRecord }> {
  const res = await fetch(url("/api/lessons"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { lesson?: TeacherLessonRecord; error?: string };
  if (!res.ok) throw new Error(data.error ?? `POST /api/lessons failed (${res.status})`);
  if (!data.lesson) throw new Error("Create library lesson response incomplete");
  return data as { lesson: TeacherLessonRecord };
}

export async function getTeacherLessons(
  bearer: string,
  courseId: string,
  params?: { limit?: number; offset?: number }
): Promise<{ lessons: TeacherLessonRecord[]; page: { limit: number; offset: number; total: number } }> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const q = qs.toString();
  const res = await fetch(
    url(`/api/courses/${encodeURIComponent(courseId)}/lessons${q ? `?${q}` : ""}`),
    { headers: { Authorization: `Bearer ${bearer}` } }
  );
  const data = (await res.json().catch(() => ({}))) as
    | { lessons?: TeacherLessonRecord[]; page?: { limit: number; offset: number; total: number }; error?: string }
    | undefined;
  if (!res.ok) {
    throw new Error(data?.error ?? `GET lessons failed (${res.status})`);
  }
  if (!data?.lessons || !data.page) throw new Error("List lessons response incomplete");
  return data as { lessons: TeacherLessonRecord[]; page: { limit: number; offset: number; total: number } };
}

export async function postTeacherLesson(
  bearer: string,
  courseId: string,
  body: {
    lessonId?: string;
    title?: string;
    slug?: string;
    position?: number;
    insertAfterPlanItemId?: string | null;
    starterFilesJson?: unknown;
    packagePolicyJson?: unknown;
  }
): Promise<{ lesson: TeacherLessonRecord }> {
  const res = await fetch(url(`/api/courses/${encodeURIComponent(courseId)}/lessons`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { lesson?: TeacherLessonRecord; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `POST lesson failed (${res.status})`);
  }
  if (!data.lesson) throw new Error("Create lesson response incomplete");
  return data as { lesson: TeacherLessonRecord };
}

export async function removeTemplateLesson(
  bearer: string,
  courseId: string,
  lessonId: string
): Promise<{ courseId: string; removedLessonId: string }> {
  const res = await fetch(
    url(`/api/courses/${encodeURIComponent(courseId)}/lessons/${encodeURIComponent(lessonId)}`),
    { method: "DELETE", headers: { Authorization: `Bearer ${bearer}` } }
  );
  const data = (await res.json().catch(() => ({}))) as {
    courseId?: string;
    removedLessonId?: string;
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Remove template lesson failed (${res.status})`);
  if (!data.removedLessonId) throw new Error("Remove template lesson response incomplete");
  return data as { courseId: string; removedLessonId: string };
}

export async function putTemplateLessonOrder(
  bearer: string,
  courseId: string,
  lessonIds: string[],
  opts?: { changelog?: string | null }
): Promise<{ courseId: string; lessonIds: string[] }> {
  const res = await fetch(
    url(`/api/courses/${encodeURIComponent(courseId)}/lesson-order`),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
      body: JSON.stringify({ lessonIds, ...(opts?.changelog ? { changelog: opts.changelog } : {}) }),
    }
  );
  const data = (await res.json().catch(() => ({}))) as {
    courseId?: string;
    lessonIds?: string[];
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Reorder template lessons failed (${res.status})`);
  if (!data.lessonIds) throw new Error("Reorder template lessons response incomplete");
  return data as { courseId: string; lessonIds: string[] };
}

export type CoursePlanItemKind = "lesson" | "quiz";

export interface TeacherQuizQuestion {
  id: string;
  prompt: string;
  options: string[];
  correctOptionIndex: number;
  sourceLessonId?: string;
  topicTags?: string[];
  rationale?: string;
  optionExplanations?: string[];
}

export interface TeacherQuizRecord {
  id: string;
  courseId: string | null;
  cohortId?: string | null;
  title: string;
  description: string | null;
  creationPrompt: string | null;
  displayQuestionCount: number;
  poolSize: number;
  questions: TeacherQuizQuestion[];
  iconName?: string | null;
  iconBgColor?: string | null;
  iconFgColor?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CoursePlanLessonItem {
  planItemId: string;
  itemKind: "lesson";
  position: number;
  lesson: TeacherLessonRecord;
}

export interface CoursePlanQuizItem {
  planItemId: string;
  itemKind: "quiz";
  position: number;
  quiz: TeacherQuizRecord;
}

export type CoursePlanItem = CoursePlanLessonItem | CoursePlanQuizItem;

export interface QuizAiDraft {
  title: string;
  description?: string;
  displayQuestionCount: number;
  questions: Omit<TeacherQuizQuestion, "id">[];
  iconName?: string;
  iconBgColor?: string;
  iconFgColor?: string;
}

export async function getCoursePlanItems(
  bearer: string,
  courseId: string
): Promise<{ courseId: string; items: CoursePlanItem[] }> {
  const { res, data } = await getJsonWithTransientRetries<{
    courseId?: string;
    items?: CoursePlanItem[];
    error?: string;
  }>(`/api/courses/${encodeURIComponent(courseId)}/plan-items`, bearer);
  if (!res.ok) throw new Error(data?.error ?? `GET plan-items failed (${res.status})`);
  if (!data?.items) throw new Error("Plan items response incomplete");
  return { courseId: data.courseId ?? courseId, items: data.items };
}

export async function putCoursePlanOrder(
  bearer: string,
  courseId: string,
  planItemIds: string[],
  opts?: { changelog?: string | null }
): Promise<{ courseId: string; planItemIds: string[] }> {
  const res = await fetch(url(`/api/courses/${encodeURIComponent(courseId)}/plan-order`), {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify({ planItemIds, ...(opts?.changelog ? { changelog: opts.changelog } : {}) }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    courseId?: string;
    planItemIds?: string[];
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Reorder plan failed (${res.status})`);
  if (!data.planItemIds) throw new Error("Reorder plan response incomplete");
  return { courseId: data.courseId ?? courseId, planItemIds: data.planItemIds };
}

export async function postTeacherQuiz(
  bearer: string,
  courseId: string,
  body: {
    title: string;
    description?: string | null;
    creationPrompt?: string | null;
    displayQuestionCount?: number;
    questions?: TeacherQuizQuestion[];
    iconName?: string | null;
    iconBgColor?: string | null;
    iconFgColor?: string | null;
    insertAfterPlanItemId?: string | null;
    injectCohortIds?: string[];
  }
): Promise<{
  quiz: TeacherQuizRecord;
  injected?: Array<{ cohortId: string; assignmentId: string }>;
}> {
  const res = await fetch(url(`/api/courses/${encodeURIComponent(courseId)}/quizzes`), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    quiz?: TeacherQuizRecord;
    injected?: Array<{ cohortId: string; assignmentId: string }>;
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `POST quiz failed (${res.status})`);
  if (!data.quiz) throw new Error("POST quiz response incomplete");
  return { quiz: data.quiz, injected: data.injected };
}

export interface QuizCohortInjectionCandidate {
  cohortId: string;
  label: string;
  anchorState: "planned" | "assigned";
}

export async function getQuizCohortInjectionCandidates(
  bearer: string,
  courseId: string,
  insertAfterPlanItemId: string | null | undefined
): Promise<{
  anchorLesson: { id: string; title: string } | null;
  candidates: QuizCohortInjectionCandidate[];
}> {
  const qs = new URLSearchParams();
  if (insertAfterPlanItemId === null) {
    qs.set("insertAfterPlanItemId", "null");
  } else if (insertAfterPlanItemId !== undefined) {
    qs.set("insertAfterPlanItemId", insertAfterPlanItemId);
  }
  const q = qs.toString();
  const { res, data } = await getJsonWithTransientRetries<{
    anchorLesson?: { id: string; title: string } | null;
    candidates?: QuizCohortInjectionCandidate[];
    error?: string;
  }>(
    `/api/courses/${encodeURIComponent(courseId)}/quiz-cohort-injection-candidates${q ? `?${q}` : ""}`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `GET quiz injection candidates failed (${res.status})`);
  if (!Array.isArray(data.candidates)) throw new Error("Quiz injection candidates response incomplete");
  return {
    anchorLesson: data.anchorLesson ?? null,
    candidates: data.candidates,
  };
}

export async function postCohortQuiz(
  bearer: string,
  cohortId: string,
  body: {
    title: string;
    description?: string | null;
    creationPrompt?: string | null;
    displayQuestionCount?: number;
    questions?: TeacherQuizQuestion[];
    iconName?: string | null;
    iconBgColor?: string | null;
    iconFgColor?: string | null;
    insertAfterAssignmentId?: string | null;
  }
): Promise<{ quiz: TeacherQuizRecord; assignment: TeacherCohortAssignmentRecord }> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}/quizzes`), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    quiz?: TeacherQuizRecord;
    assignment?: TeacherCohortAssignmentRecord;
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `POST cohort quiz failed (${res.status})`);
  if (!data.quiz || !data.assignment) throw new Error("POST cohort quiz response incomplete");
  return { quiz: data.quiz, assignment: data.assignment };
}

export async function getTeacherQuiz(
  bearer: string,
  courseId: string,
  quizId: string
): Promise<{ quiz: TeacherQuizRecord }> {
  const { res, data } = await getJsonWithTransientRetries<{ quiz?: TeacherQuizRecord; error?: string }>(
    `/api/courses/${encodeURIComponent(courseId)}/quizzes/${encodeURIComponent(quizId)}`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `GET quiz failed (${res.status})`);
  if (!data?.quiz) throw new Error("GET quiz response incomplete");
  return { quiz: data.quiz };
}

export async function patchTeacherQuiz(
  bearer: string,
  courseId: string,
  quizId: string,
  body: Partial<{
    title: string;
    description: string | null;
    creationPrompt: string | null;
    displayQuestionCount: number;
    questions: TeacherQuizQuestion[];
    iconName: string | null;
    iconBgColor: string | null;
    iconFgColor: string | null;
  }>
): Promise<{ quiz: TeacherQuizRecord }> {
  const res = await fetch(
    url(`/api/courses/${encodeURIComponent(courseId)}/quizzes/${encodeURIComponent(quizId)}`),
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
      body: JSON.stringify(body),
    }
  );
  const data = (await res.json().catch(() => ({}))) as { quiz?: TeacherQuizRecord; error?: string };
  if (!res.ok) throw new Error(data.error ?? `PATCH quiz failed (${res.status})`);
  if (!data.quiz) throw new Error("PATCH quiz response incomplete");
  return { quiz: data.quiz };
}

export async function deleteTeacherQuiz(
  bearer: string,
  courseId: string,
  quizId: string
): Promise<{ courseId: string; removedQuizId: string }> {
  const res = await fetch(
    url(`/api/courses/${encodeURIComponent(courseId)}/quizzes/${encodeURIComponent(quizId)}`),
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${bearer}` },
    }
  );
  const data = (await res.json().catch(() => ({}))) as {
    courseId?: string;
    removedQuizId?: string;
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `DELETE quiz failed (${res.status})`);
  if (!data.removedQuizId) throw new Error("DELETE quiz response incomplete");
  return { courseId: data.courseId ?? courseId, removedQuizId: data.removedQuizId };
}

export async function postQuizAiGenerate(
  bearer: string,
  courseId: string,
  body: { prompt?: string; insertAfterPlanItemId?: string | null }
): Promise<{ jobId: string; status: string }> {
  const res = await fetch(url(`/api/courses/${encodeURIComponent(courseId)}/quizzes/ai/generate`), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { jobId?: string; status?: string; error?: string };
  if (!res.ok) throw new Error(data.error ?? `Quiz AI generate failed (${res.status})`);
  if (!data.jobId) throw new Error("Quiz AI generate response incomplete");
  return { jobId: data.jobId, status: data.status ?? "pending" };
}

export async function getQuizAiJob(
  bearer: string,
  courseId: string,
  jobId: string
): Promise<{ jobId: string; status: string; draft?: QuizAiDraft; error?: string }> {
  const { res, data } = await getJsonWithTransientRetries<{
    jobId?: string;
    status?: string;
    draft?: QuizAiDraft;
    error?: string;
  }>(`/api/courses/${encodeURIComponent(courseId)}/quizzes/ai/jobs/${encodeURIComponent(jobId)}`, bearer);
  if (!res.ok) throw new Error(data?.error ?? `Quiz AI job failed (${res.status})`);
  return {
    jobId: data?.jobId ?? jobId,
    status: data?.status ?? "pending",
    draft: data?.draft,
    error: data?.error,
  };
}

export async function pollQuizAiJob(
  bearer: string,
  courseId: string,
  jobId: string,
  opts?: { intervalMs?: number; timeoutMs?: number }
): Promise<QuizAiDraft> {
  const intervalMs = opts?.intervalMs ?? 2000;
  const timeoutMs = opts?.timeoutMs ?? 120_000;
  const start = Date.now();
  for (;;) {
    const job = await getQuizAiJob(bearer, courseId, jobId);
    if (job.status === "done" && job.draft) return job.draft;
    if (job.status === "failed") throw new Error(job.error ?? "Quiz AI generation failed");
    if (Date.now() - start > timeoutMs) throw new Error("Quiz AI generation timed out");
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

const libraryLessonPath = (lessonId: string) => `/api/lessons/${encodeURIComponent(lessonId)}`;

export interface LessonStandardLink {
  id: string;
  code: string;
  description: string;
  standardSetId: string;
  standardSetName: string;
  retired: boolean;
}

export interface LessonGlossaryLink {
  id: string;
  term: string;
  definition: string;
  standardSetId: string;
  standardSetName: string;
  retired: boolean;
}

export async function getTeacherLessonDetail(
  bearer: string,
  _courseId: string | undefined,
  lessonId: string
): Promise<{
  lesson: TeacherLessonRecord;
  tourSteps: TeacherTourStepRecord[];
  preview: { mainPy: string };
  usedInTemplates?: number;
  activeCohorts?: number;
  standards?: LessonStandardLink[];
  glossary?: LessonGlossaryLink[];
}> {
  const res = await fetch(url(libraryLessonPath(lessonId)), { headers: { Authorization: `Bearer ${bearer}` } });
  const data = (await res.json().catch(() => ({}))) as
    | {
        lesson?: TeacherLessonRecord;
        tourSteps?: TeacherTourStepRecord[];
        preview?: { mainPy: string };
        usedInTemplates?: number;
        activeCohorts?: number;
        standards?: LessonStandardLink[];
        glossary?: LessonGlossaryLink[];
        error?: string;
      }
    | undefined;
  if (!res.ok) {
    throw new Error(data?.error ?? `GET lesson failed (${res.status})`);
  }
  if (!data?.lesson || !data.tourSteps || !data.preview) throw new Error("Lesson detail response incomplete");
  return data as {
    lesson: TeacherLessonRecord;
    tourSteps: TeacherTourStepRecord[];
    preview: { mainPy: string };
    usedInTemplates?: number;
    activeCohorts?: number;
    standards?: LessonStandardLink[];
    glossary?: LessonGlossaryLink[];
  };
}

export async function patchTeacherLesson(
  bearer: string,
  _courseId: string | undefined,
  lessonId: string,
  body: {
    title?: string;
    slug?: string | null;
    starterFilesJson?: unknown;
    packagePolicyJson?: unknown;
    changelog?: string | null;
    isDraft?: boolean;
  } & LessonPresentationFields
): Promise<{ lesson: TeacherLessonRecord }> {
  const res = await fetch(url(libraryLessonPath(lessonId)), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { lesson?: TeacherLessonRecord; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `PATCH lesson failed (${res.status})`);
  }
  if (!data.lesson) throw new Error("Patch lesson response incomplete");
  return data as { lesson: TeacherLessonRecord };
}

export async function deleteLibraryLesson(
  bearer: string,
  lessonId: string
): Promise<{ deletedId: string; removedFromTemplates?: number; activeCohorts?: number }> {
  const res = await fetch(url(libraryLessonPath(lessonId)), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    deletedId?: string;
    removedFromTemplates?: number;
    activeCohorts?: number;
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Delete lesson failed (${res.status})`);
  if (!data.deletedId) throw new Error("Delete lesson response incomplete");
  return data as { deletedId: string; removedFromTemplates?: number; activeCohorts?: number };
}

export async function restoreLibraryLesson(
  bearer: string,
  lessonId: string
): Promise<{ lesson: TeacherLessonRecord }> {
  const res = await fetch(url(`${libraryLessonPath(lessonId)}/restore`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { lesson?: TeacherLessonRecord; error?: string };
  if (!res.ok) throw new Error(data.error ?? `Restore lesson failed (${res.status})`);
  if (!data.lesson) throw new Error("Restore lesson response incomplete");
  return data as { lesson: TeacherLessonRecord };
}

export async function permanentDeleteLibraryLesson(
  bearer: string,
  lessonId: string
): Promise<{ deletedId: string; permanent: true }> {
  const res = await fetch(url(`${libraryLessonPath(lessonId)}?permanent=true`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { deletedId?: string; permanent?: boolean; error?: string };
  if (!res.ok) throw new Error(data.error ?? `Permanent delete failed (${res.status})`);
  if (!data.deletedId) throw new Error("Permanent delete response incomplete");
  return data as { deletedId: string; permanent: true };
}

export async function putTeacherLessonTourSteps(
  bearer: string,
  _courseId: string | undefined,
  lessonId: string,
  steps: { title: string; body: string[]; referenceCode?: string | null }[]
): Promise<{ tourSteps: TeacherTourStepRecord[] }> {
  const res = await fetch(url(`${libraryLessonPath(lessonId)}/tour-steps`), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify({ steps }),
  });
  const data = (await res.json().catch(() => ({}))) as { tourSteps?: TeacherTourStepRecord[]; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `PUT tour-steps failed (${res.status})`);
  }
  if (!data.tourSteps) throw new Error("Tour steps response incomplete");
  return data as { tourSteps: TeacherTourStepRecord[] };
}

const lessonTestsPath = (lessonId: string) => `${libraryLessonPath(lessonId)}/tests`;

export async function getTeacherLessonTests(
  bearer: string,
  _courseId: string | undefined,
  lessonId: string
): Promise<{ tests: TeacherLessonTestRecord[] }> {
  const res = await fetch(url(lessonTestsPath(lessonId)), {
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { tests?: TeacherLessonTestRecord[]; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `GET lesson tests failed (${res.status})`);
  }
  if (!data.tests) throw new Error("Lesson tests response incomplete");
  return data as { tests: TeacherLessonTestRecord[] };
}

export async function postTeacherLessonTest(
  bearer: string,
  _courseId: string | undefined,
  lessonId: string,
  body: {
    name: string;
    ordinal?: number;
    isRequired?: boolean;
    definitionJson?: unknown;
    visibility?: TeacherLessonTestVisibility;
    description?: string | null;
    testCode?: string;
  }
): Promise<{ test: TeacherLessonTestRecord }> {
  const res = await fetch(url(lessonTestsPath(lessonId)), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { test?: TeacherLessonTestRecord; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `POST lesson test failed (${res.status})`);
  }
  if (!data.test) throw new Error("Create lesson test response incomplete");
  return data as { test: TeacherLessonTestRecord };
}

export async function patchTeacherLessonTest(
  bearer: string,
  _courseId: string | undefined,
  lessonId: string,
  testId: string,
  body: {
    ordinal?: number;
    name?: string;
    isRequired?: boolean;
    definitionJson?: unknown;
    visibility?: TeacherLessonTestVisibility;
    description?: string | null;
    testCode?: string;
  }
): Promise<{ test: TeacherLessonTestRecord }> {
  const res = await fetch(url(`${lessonTestsPath(lessonId)}/${encodeURIComponent(testId)}`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { test?: TeacherLessonTestRecord; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `PATCH lesson test failed (${res.status})`);
  }
  if (!data.test) throw new Error("Patch lesson test response incomplete");
  return data as { test: TeacherLessonTestRecord };
}

export async function deleteTeacherLessonTest(
  bearer: string,
  _courseId: string | undefined,
  lessonId: string,
  testId: string
): Promise<{ deletedId: string }> {
  const res = await fetch(url(`${lessonTestsPath(lessonId)}/${encodeURIComponent(testId)}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { deletedId?: string; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `DELETE lesson test failed (${res.status})`);
  }
  if (!data.deletedId) throw new Error("Delete lesson test response incomplete");
  return data as { deletedId: string };
}

export interface TeacherCohortRecord {
  id: string;
  label: string;
  courseId: string;
  courseTitle: string;
  courseHeroImageUrl?: string | null;
  courseAccentColor?: string | null;
  status: "active" | "archived";
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  activeJoinCode: string | null;
  studentCount?: number;
  lessonProgress?: { planned: number; assigned: number; completed: number };
}

export type CohortEventType =
  | "cohort_created"
  | "cohort_concluded"
  | "student_joined"
  | "student_removed"
  | "lesson_released"
  | "lesson_closed"
  | "student_submitted"
  | "quiz_submitted"
  | "student_gave_up"
  | "join_code_issued"
  | "join_code_disabled";

export interface CohortEventRecord {
  id: string;
  eventType: CohortEventType;
  occurredAt: string;
  cohortId: string;
  cohortLabel: string;
  studentUserId: string | null;
  studentEmail: string | null;
  lessonId: string | null;
  lessonTitle: string | null;
  assignmentId: string | null;
  resourceId: string | null;
  detail: string | null;
}

export interface TeacherJoinCodeRecord {
  id: string;
  code: string;
  disabledAt: string | null;
  createdAt: string;
  rotatedAt: string | null;
}

export interface TeacherCohortAssignmentRecord {
  id: string;
  itemKind?: "lesson" | "quiz";
  lessonId: string | null;
  quizId?: string | null;
  lessonTitle: string;
  itemTitle?: string;
  courseId: string;
  courseTitle: string;
  released: boolean;
  closed: boolean;
  releasedAt: string | null;
  closedAt: string | null;
  dueAt: string | null;
  planPosition?: number | null;
  workspaceMode?: LessonWorkspaceMode;
  createdAt: string;
  updatedAt: string;
  iconName?: string | null;
  iconBgColor?: string | null;
  iconFgColor?: string | null;
}

export interface TeacherRosterEnrollment {
  enrollmentId: string;
  studentUserId: string;
  email: string | null;
  googleSub: string | null;
  status: string;
  createdAt: string;
  removedAt: string | null;
}

export async function getTeacherCohorts(
  bearer: string,
  params?: { limit?: number; offset?: number }
): Promise<{ cohorts: TeacherCohortRecord[]; page: { limit: number; offset: number; total: number } }> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const q = qs.toString();
  const { res, data } = await getJsonWithTransientRetries<{
    cohorts?: TeacherCohortRecord[];
    page?: { limit: number; offset: number; total: number };
  }>(`/api/cohorts${q ? `?${q}` : ""}`, bearer);
  if (!res.ok) {
    throw new Error(data?.error ?? `GET /api/cohorts failed (${res.status})`);
  }
  if (!data?.cohorts || !data.page) throw new Error("List cohorts response incomplete");
  return data as { cohorts: TeacherCohortRecord[]; page: { limit: number; offset: number; total: number } };
}

export async function getTeacherCohortEventLog(
  bearer: string,
  params?: { cohortId?: string; limit?: number; offset?: number }
): Promise<{ events: CohortEventRecord[]; page: { limit: number; offset: number } }> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const path = params?.cohortId
    ? `/api/cohorts/${encodeURIComponent(params.cohortId)}/event-log`
    : `/api/cohorts/event-log`;
  const q = qs.toString();
  const { res, data } = await getJsonWithTransientRetries<{
    events?: CohortEventRecord[];
    page?: { limit: number; offset: number };
    error?: string;
  }>(`${path}${q ? `?${q}` : ""}`, bearer);
  if (!res.ok) {
    throw new Error(data?.error ?? `GET cohort event log failed (${res.status})`);
  }
  if (!data?.events || !data.page) throw new Error("Cohort event log response incomplete");
  return data as { events: CohortEventRecord[]; page: { limit: number; offset: number } };
}

const EVENT_LOG_PAGE_SIZE = 100;

/** Walk every page of the event log (API max 100 per request). */
export async function fetchAllTeacherCohortEvents(
  bearer: string,
  params?: { cohortId?: string }
): Promise<CohortEventRecord[]> {
  const all: CohortEventRecord[] = [];
  let offset = 0;

  for (;;) {
    const data = await getTeacherCohortEventLog(bearer, {
      cohortId: params?.cohortId,
      limit: EVENT_LOG_PAGE_SIZE,
      offset,
    });
    all.push(...data.events);
    if (data.events.length < EVENT_LOG_PAGE_SIZE) break;
    offset += data.events.length;
  }

  return all;
}

export async function postTeacherCohort(
  bearer: string,
  body: { courseId: string; label: string; seedLessonPlans?: boolean }
): Promise<{ cohort: TeacherCohortRecord; lessonPlanSync?: { added: number; skippedAssigned: number } }> {
  const res = await fetch(url("/api/cohorts"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    cohort?: TeacherCohortRecord;
    lessonPlanSync?: { added: number; skippedAssigned: number };
    error?: string;
  };
  if (!res.ok) {
    throw new ApiClientError(data.error ?? `POST /api/cohorts failed (${res.status})`, {
      code: (data as { code?: string }).code,
      status: res.status,
    });
  }
  if (!data.cohort) throw new Error("Cohort create response incomplete");
  return data as { cohort: TeacherCohortRecord; lessonPlanSync?: { added: number; skippedAssigned: number } };
}

export type TeacherAccessPhase = "paid" | "app_trial" | "expired" | "none";

export interface TeacherBillingSubscriptionResponse {
  accessPhase: TeacherAccessPhase;
  subscription: {
    status: string;
    priceId: string | null;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  };
  plan: {
    interval: "month" | "year";
    intervalCount: number;
    unitAmount: number;
    currency: string;
  } | null;
  appTrial: {
    startedAt: string | null;
    endsAt: string | null;
  };
  limits: {
    activeCohorts: number | null;
    activeLessons: number | null;
    studentsPerCohort: number | null;
    activeStudents: number | null;
    aiRequestsPerPeriod: number | null;
  };
  usage: {
    periodKey: string;
    aiLessonRequestsUsed: number;
    activeCohortsUsed: number;
    activeLessonsUsed: number;
    activeStudentsUsed: number;
    activeCoursesUsed: number;
    playgroundProjectsUsed: number;
  };
}

export async function getTeacherBillingSubscription(
  bearer: string
): Promise<TeacherBillingSubscriptionResponse> {
  const res = await fetch(url("/api/billing/subscription"), {
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as TeacherBillingSubscriptionResponse & {
    error?: string;
    code?: string;
  };
  if (!res.ok) {
    throw new ApiClientError(data.error ?? `GET /api/billing/subscription failed (${res.status})`, {
      code: data.code,
      status: res.status,
    });
  }
  return data;
}

export async function postTeacherBillingCheckoutSession(
  bearer: string,
  body: { plan: "monthly" | "annual" }
): Promise<{ checkoutUrl: string }> {
  const res = await fetch(url("/api/billing/checkout-session"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { checkoutUrl?: string; error?: string; code?: string };
  if (!res.ok) {
    throw new ApiClientError(data.error ?? `POST /api/billing/checkout-session failed (${res.status})`, {
      code: data.code,
      status: res.status,
    });
  }
  if (!data.checkoutUrl) throw new Error("Checkout response incomplete");
  return { checkoutUrl: data.checkoutUrl };
}

export async function postTeacherBillingPortalSession(
  bearer: string
): Promise<{ portalUrl: string }> {
  const res = await fetch(url("/api/billing/portal-session"), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { portalUrl?: string; error?: string; code?: string };
  if (!res.ok) {
    throw new ApiClientError(data.error ?? `POST /api/billing/portal-session failed (${res.status})`, {
      code: data.code,
      status: res.status,
    });
  }
  if (!data.portalUrl) throw new Error("Portal response incomplete");
  return { portalUrl: data.portalUrl };
}

export async function postTeacherCohortSyncLessonPlans(
  bearer: string,
  cohortId: string
): Promise<{ added: number; skippedAssigned: number }> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}/sync-lesson-plans`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    added?: number;
    skippedAssigned?: number;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `POST sync lesson plans failed (${res.status})`);
  }
  if (typeof data.added !== "number" || typeof data.skippedAssigned !== "number") {
    throw new Error("Sync lesson plans response incomplete");
  }
  return { added: data.added, skippedAssigned: data.skippedAssigned };
}

export async function getTeacherCohort(
  bearer: string,
  cohortId: string
): Promise<{ cohort: TeacherCohortRecord }> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}`), {
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { cohort?: TeacherCohortRecord; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `GET cohort failed (${res.status})`);
  }
  if (!data.cohort) throw new Error("cohort response incomplete");
  return data as { cohort: TeacherCohortRecord };
}

export async function patchTeacherCohort(
  bearer: string,
  cohortId: string,
  body: { label?: string; archived?: boolean }
): Promise<{ cohort: TeacherCohortRecord }> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { cohort?: TeacherCohortRecord; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `PATCH cohort failed (${res.status})`);
  }
  if (!data.cohort) throw new Error("cohort patch incomplete");
  return data as { cohort: TeacherCohortRecord };
}

export async function deleteTeacherCohort(
  bearer: string,
  cohortId: string
): Promise<{ deletedCohortId: string }> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    deletedCohortId?: string;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `DELETE cohort failed (${res.status})`);
  }
  if (!data.deletedCohortId) throw new Error("cohort delete incomplete");
  return { deletedCohortId: data.deletedCohortId };
}

export async function getTeacherCohortAssignments(
  bearer: string,
  cohortId: string
): Promise<{ assignments: TeacherCohortAssignmentRecord[] }> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}/assignments`), {
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as
    | { assignments?: TeacherCohortAssignmentRecord[]; error?: string }
    | undefined;
  if (!res.ok) {
    throw new Error(data?.error ?? `GET cohort assignments failed (${res.status})`);
  }
  if (!data?.assignments) throw new Error("Assignments response incomplete");
  return data as { assignments: TeacherCohortAssignmentRecord[] };
}

export async function putCohortAssignmentOrder(
  bearer: string,
  cohortId: string,
  assignmentIds: string[]
): Promise<{ cohortId: string; assignmentIds: string[] }> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}/assignment-order`), {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify({ assignmentIds }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    cohortId?: string;
    assignmentIds?: string[];
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Reorder cohort plan failed (${res.status})`);
  if (!data.assignmentIds) throw new Error("Reorder cohort plan response incomplete");
  return { cohortId: data.cohortId ?? cohortId, assignmentIds: data.assignmentIds };
}

export async function getTeacherCohortQuiz(
  bearer: string,
  cohortId: string,
  quizId: string
): Promise<{ quiz: TeacherQuizRecord }> {
  const { res, data } = await getJsonWithTransientRetries<{ quiz?: TeacherQuizRecord; error?: string }>(
    `/api/cohorts/${encodeURIComponent(cohortId)}/quizzes/${encodeURIComponent(quizId)}`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `GET cohort quiz failed (${res.status})`);
  if (!data?.quiz) throw new Error("GET cohort quiz response incomplete");
  return { quiz: data.quiz };
}

export async function postTeacherCohortAssignment(
  bearer: string,
  cohortId: string,
  body: {
    lessonId?: string;
    quizId?: string;
    released?: boolean;
    dueAt?: string | null;
    insertAfterAssignmentId?: string | null;
  }
): Promise<{ assignment: TeacherCohortAssignmentRecord }> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}/assignments`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as
    | { assignment?: TeacherCohortAssignmentRecord; error?: string }
    | undefined;
  if (!res.ok) {
    throw new Error(data?.error ?? `POST cohort assignment failed (${res.status})`);
  }
  if (!data?.assignment) throw new Error("Assignment create response incomplete");
  return data as { assignment: TeacherCohortAssignmentRecord };
}

export async function patchTeacherCohortAssignment(
  bearer: string,
  cohortId: string,
  assignmentId: string,
  body: { released?: boolean; dueAt?: string | null; closed?: boolean }
): Promise<{ assignment: TeacherCohortAssignmentRecord }> {
  const res = await fetch(
    url(`/api/cohorts/${encodeURIComponent(cohortId)}/assignments/${encodeURIComponent(assignmentId)}`),
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearer}`,
      },
      body: JSON.stringify(body),
    }
  );
  const data = (await res.json().catch(() => ({}))) as
    | { assignment?: TeacherCohortAssignmentRecord; error?: string }
    | undefined;
  if (!res.ok) {
    throw new Error(data?.error ?? `PATCH cohort assignment failed (${res.status})`);
  }
  if (!data?.assignment) throw new Error("Assignment patch response incomplete");
  return data as { assignment: TeacherCohortAssignmentRecord };
}

export async function getTeacherCohortAssignment(
  bearer: string,
  cohortId: string,
  assignmentId: string
): Promise<{ assignment: TeacherCohortAssignmentRecord }> {
  const res = await fetch(
    url(`/api/cohorts/${encodeURIComponent(cohortId)}/assignments/${encodeURIComponent(assignmentId)}`),
    { headers: { Authorization: `Bearer ${bearer}` } }
  );
  const data = (await res.json().catch(() => ({}))) as
    | { assignment?: TeacherCohortAssignmentRecord; error?: string }
    | undefined;
  if (!res.ok) {
    throw new Error(data?.error ?? `GET assignment failed (${res.status})`);
  }
  if (!data?.assignment) throw new Error("Assignment response incomplete");
  return data as { assignment: TeacherCohortAssignmentRecord };
}

export async function deleteTeacherCohortAssignment(
  bearer: string,
  cohortId: string,
  assignmentId: string
): Promise<{ deletedId: string }> {
  const res = await fetch(
    url(`/api/cohorts/${encodeURIComponent(cohortId)}/assignments/${encodeURIComponent(assignmentId)}`),
    { method: "DELETE", headers: { Authorization: `Bearer ${bearer}` } }
  );
  const data = (await res.json().catch(() => ({}))) as { deletedId?: string; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `DELETE assignment failed (${res.status})`);
  }
  if (!data.deletedId) throw new Error("DELETE assignment response incomplete");
  return data as { deletedId: string };
}

export async function postTeacherAssignmentReleaseShortcut(
  bearer: string,
  cohortId: string,
  assignmentId: string
): Promise<{ assignment: TeacherCohortAssignmentRecord }> {
  const res = await fetch(
    url(`/api/cohorts/${encodeURIComponent(cohortId)}/assignments/${encodeURIComponent(assignmentId)}/release`),
    {
      method: "POST",
      headers: { Authorization: `Bearer ${bearer}` },
    }
  );
  const data = (await res.json().catch(() => ({}))) as
    | { assignment?: TeacherCohortAssignmentRecord; error?: string }
    | undefined;
  if (!res.ok) {
    throw new Error(data?.error ?? `POST release shortcut failed (${res.status})`);
  }
  if (!data?.assignment) throw new Error("Release response incomplete");
  return data as { assignment: TeacherCohortAssignmentRecord };
}

export async function postTeacherAssignmentCloseShortcut(
  bearer: string,
  cohortId: string,
  assignmentId: string
): Promise<{ assignment: TeacherCohortAssignmentRecord }> {
  const res = await fetch(
    url(`/api/cohorts/${encodeURIComponent(cohortId)}/assignments/${encodeURIComponent(assignmentId)}/close`),
    {
      method: "POST",
      headers: { Authorization: `Bearer ${bearer}` },
    }
  );
  const data = (await res.json().catch(() => ({}))) as
    | { assignment?: TeacherCohortAssignmentRecord; error?: string }
    | undefined;
  if (!res.ok) {
    throw new Error(data?.error ?? `POST close shortcut failed (${res.status})`);
  }
  if (!data?.assignment) throw new Error("Close response incomplete");
  return data as { assignment: TeacherCohortAssignmentRecord };
}

export interface TeacherCohortAssignmentProgressRow {
  assignmentId: string;
  itemKind: "lesson" | "quiz";
  lessonId: string | null;
  quizId: string | null;
  lessonTitle: string;
  pairingCount: number;
  notStarted: number;
  inProgress: number;
  needsHelp: number;
  complete: number;
  rosterTotal: number;
}

export interface TeacherCohortQuizResultAttempt {
  studentUserId: string;
  email: string;
  attemptNumber: number;
  scorePercent: number;
  correctCount: number;
  totalCount: number;
  createdAt: string;
}

export async function getTeacherCohortQuizResults(
  bearer: string,
  cohortId: string,
  quizId: string,
  opts?: { studentUserId?: string }
): Promise<{
  cohortId: string;
  quizId: string;
  quizTitle: string;
  assignmentId: string;
  attempts: TeacherCohortQuizResultAttempt[];
}> {
  const qs = opts?.studentUserId
    ? `?studentUserId=${encodeURIComponent(opts.studentUserId)}`
    : "";
  const res = await fetch(
    url(`/api/cohorts/${encodeURIComponent(cohortId)}/quizzes/${encodeURIComponent(quizId)}/results${qs}`),
    { headers: { Authorization: `Bearer ${bearer}` } }
  );
  const data = (await res.json().catch(() => ({}))) as {
    cohortId?: string;
    quizId?: string;
    quizTitle?: string;
    assignmentId?: string;
    attempts?: TeacherCohortQuizResultAttempt[];
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `GET quiz results failed (${res.status})`);
  }
  if (
    typeof data.cohortId !== "string" ||
    typeof data.quizId !== "string" ||
    typeof data.quizTitle !== "string" ||
    typeof data.assignmentId !== "string" ||
    !Array.isArray(data.attempts)
  ) {
    throw new Error("quiz results response incomplete");
  }
  return data as {
    cohortId: string;
    quizId: string;
    quizTitle: string;
    assignmentId: string;
    attempts: TeacherCohortQuizResultAttempt[];
  };
}

export async function getTeacherCohortAssignmentProgress(
  bearer: string,
  cohortId: string
): Promise<{
  cohortId: string;
  rosterTotal: number;
  assignments: TeacherCohortAssignmentProgressRow[];
}> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}/assignment-progress`), {
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    cohortId?: string;
    rosterTotal?: number;
    assignments?: TeacherCohortAssignmentProgressRow[];
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `GET assignment progress failed (${res.status})`);
  }
  if (
    typeof data.rosterTotal !== "number" ||
    !Array.isArray(data.assignments) ||
    typeof data.cohortId !== "string"
  ) {
    throw new Error("assignment progress incomplete");
  }
  return data as {
    cohortId: string;
    rosterTotal: number;
    assignments: TeacherCohortAssignmentProgressRow[];
  };
}

export interface TeacherCohortProgressMatrixAssignment {
  assignmentId: string;
  itemKind?: "lesson" | "quiz";
  lessonId: string | null;
  quizId?: string | null;
  lessonTitle: string;
  released?: boolean;
  closed: boolean;
}

export interface TeacherCohortProgressMatrixStudent {
  studentUserId: string;
  email: string;
}

export interface TeacherCohortProgressMatrixCell {
  studentUserId: string;
  assignmentId: string;
  progressStatus: "not_started" | "in_progress" | "needs_help" | "complete";
  lastOverallStatus: string | null;
  lastSubmissionKind: string | null;
  quizAttemptCount?: number | null;
  quizScoreCorrect?: number | null;
  quizScoreTotal?: number | null;
}

export async function getTeacherCohortStudentProgressMatrix(
  bearer: string,
  cohortId: string
): Promise<{
  cohortId: string;
  assignments: TeacherCohortProgressMatrixAssignment[];
  students: TeacherCohortProgressMatrixStudent[];
  cells: TeacherCohortProgressMatrixCell[];
}> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}/student-progress-matrix`), {
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    cohortId?: string;
    assignments?: TeacherCohortProgressMatrixAssignment[];
    students?: TeacherCohortProgressMatrixStudent[];
    cells?: TeacherCohortProgressMatrixCell[];
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `GET student progress matrix failed (${res.status})`);
  }
  if (
    typeof data.cohortId !== "string" ||
    !Array.isArray(data.assignments) ||
    !Array.isArray(data.students) ||
    !Array.isArray(data.cells)
  ) {
    throw new Error("Student progress matrix response incomplete");
  }
  return data as {
    cohortId: string;
    assignments: TeacherCohortProgressMatrixAssignment[];
    students: TeacherCohortProgressMatrixStudent[];
    cells: TeacherCohortProgressMatrixCell[];
  };
}

export interface TeacherLessonSubmissionAttempt {
  id: string;
  createdAt: string;
  lessonId: string;
  requiredPassed: boolean;
  optionalPassed: boolean;
  overallStatus: string;
  submissionKind: string;
  codeSnapshotS3Key: string | null;
  testResults: unknown;
  studentUserId: string;
  studentEmail: string;
}

export interface TeacherLessonSubmissionSummaryStudent {
  studentUserId: string;
  email: string;
  hasWorkspace: boolean;
  attemptCount: number;
  lastAttemptAt: string | null;
  lastOverallStatus: string | null;
  lastSubmissionKind: string | null;
  everRequiredPassed: boolean;
  progressStatus: "not_started" | "in_progress" | "needs_help" | "complete";
  credited: boolean;
}

export interface TeacherLessonSubmissionSummary {
  cohortId: string;
  lessonId: string;
  rosterActive: number;
  counts: {
    notStarted: number;
    inProgress: number;
    needsHelp: number;
    attemptedGiveUp: number;
    completed: number;
  };
  students: TeacherLessonSubmissionSummaryStudent[];
}

export interface TeacherSubmissionSnapshot {
  cohortId: string;
  lessonId: string;
  attemptId: string;
  studentUserId: string;
  studentEmail: string;
  createdAt: string;
  overallStatus: string;
  submissionKind: string;
  requiredPassed: boolean;
  optionalPassed: boolean;
  testResults: unknown;
  snapshot: {
    workspaceFiles: unknown;
    validationEngine: string | null;
    submittedAt: string | null;
    mainPy: string;
  } | null;
}

export async function getTeacherLessonSubmissions(
  bearer: string,
  cohortId: string,
  lessonId: string
): Promise<{ cohortId: string; lessonId: string; attempts: TeacherLessonSubmissionAttempt[] }> {
  const res = await fetch(
    url(`/api/cohorts/${encodeURIComponent(cohortId)}/lessons/${encodeURIComponent(lessonId)}/submissions`),
    { headers: { Authorization: `Bearer ${bearer}` } }
  );
  const data = (await res.json().catch(() => ({}))) as {
    cohortId?: string;
    lessonId?: string;
    attempts?: TeacherLessonSubmissionAttempt[];
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `GET lesson submissions failed (${res.status})`);
  if (typeof data.cohortId !== "string" || typeof data.lessonId !== "string" || !Array.isArray(data.attempts)) {
    throw new Error("lesson submissions response incomplete");
  }
  return data as { cohortId: string; lessonId: string; attempts: TeacherLessonSubmissionAttempt[] };
}

export async function getTeacherLessonSubmissionSummary(
  bearer: string,
  cohortId: string,
  lessonId: string
): Promise<TeacherLessonSubmissionSummary> {
  const res = await fetch(
    url(
      `/api/cohorts/${encodeURIComponent(cohortId)}/lessons/${encodeURIComponent(lessonId)}/submission-summary`
    ),
    { headers: { Authorization: `Bearer ${bearer}` } }
  );
  const data = (await res.json().catch(() => ({}))) as TeacherLessonSubmissionSummary & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `GET submission summary failed (${res.status})`);
  if (typeof data.cohortId !== "string" || typeof data.lessonId !== "string" || !Array.isArray(data.students)) {
    throw new Error("submission summary response incomplete");
  }
  return data;
}

export async function getTeacherSubmissionSnapshot(
  bearer: string,
  cohortId: string,
  lessonId: string,
  attemptId: string
): Promise<TeacherSubmissionSnapshot> {
  const res = await fetch(
    url(
      `/api/cohorts/${encodeURIComponent(cohortId)}/lessons/${encodeURIComponent(lessonId)}/submissions/${encodeURIComponent(attemptId)}/snapshot`
    ),
    { headers: { Authorization: `Bearer ${bearer}` } }
  );
  const data = (await res.json().catch(() => ({}))) as TeacherSubmissionSnapshot & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `GET submission snapshot failed (${res.status})`);
  if (typeof data.attemptId !== "string" || !("snapshot" in data)) {
    throw new Error("submission snapshot response incomplete");
  }
  return data;
}

export async function postTeacherCohortEnrollments(
  bearer: string,
  cohortId: string,
  body: { email?: string; emails?: string[] }
): Promise<{
  results: { email: string; ok: boolean; enrollmentId?: string; studentUserId?: string; error?: string }[];
  counts: { requested: number; succeeded: number; failed: number };
}> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}/enrollments`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    results?: { email: string; ok: boolean; enrollmentId?: string; studentUserId?: string; error?: string }[];
    counts?: { requested: number; succeeded: number; failed: number };
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `POST enrollments failed (${res.status})`);
  }
  if (!data.results || !data.counts) throw new Error("enrollment response incomplete");
  return { results: data.results, counts: data.counts };
}

export async function postTeacherCohortJoinCode(
  bearer: string,
  cohortId: string
): Promise<{ joinCode: TeacherJoinCodeRecord }> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}/join-code`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { joinCode?: TeacherJoinCodeRecord; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `POST join code failed (${res.status})`);
  }
  if (!data.joinCode) throw new Error("join code response incomplete");
  return data as { joinCode: TeacherJoinCodeRecord };
}

export async function postTeacherCohortJoinCodeDisable(
  bearer: string,
  cohortId: string
): Promise<{ disabled: boolean }> {
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}/join-code/disable`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { disabled?: boolean; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `POST disable join code failed (${res.status})`);
  }
  return data as { disabled: boolean };
}

export async function getTeacherCohortRoster(
  bearer: string,
  cohortId: string,
  params?: { limit?: number; offset?: number }
): Promise<{
  enrollments: TeacherRosterEnrollment[];
  page: { limit: number; offset: number; total: number };
}> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const q = qs.toString();
  const res = await fetch(url(`/api/cohorts/${encodeURIComponent(cohortId)}/roster${q ? `?${q}` : ""}`), {
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as
    | {
        enrollments?: TeacherRosterEnrollment[];
        page?: { limit: number; offset: number; total: number };
        error?: string;
      }
    | undefined;
  if (!res.ok) {
    throw new Error(data?.error ?? `GET roster failed (${res.status})`);
  }
  if (!data?.enrollments || !data.page) throw new Error("roster response incomplete");
  return data as {
    enrollments: TeacherRosterEnrollment[];
    page: { limit: number; offset: number; total: number };
  };
}

export async function patchTeacherRemoveEnrollment(
  bearer: string,
  cohortId: string,
  enrollmentId: string
): Promise<{ enrollment: Record<string, string | null> }> {
  const res = await fetch(
    url(`/api/cohorts/${encodeURIComponent(cohortId)}/enrollments/${encodeURIComponent(enrollmentId)}`),
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearer}`,
      },
      body: JSON.stringify({ status: "removed" }),
    }
  );
  const data = (await res.json().catch(() => ({}))) as {
    enrollment?: Record<string, string | null>;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `PATCH enrollment failed (${res.status})`);
  }
  if (!data.enrollment) throw new Error("enrollment response incomplete");
  return data as { enrollment: Record<string, string | null> };
}

export interface TeacherApiKeySummary {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export async function getTeacherApiKeys(bearer: string): Promise<{ apiKeys: TeacherApiKeySummary[] }> {
  const res = await fetch(url("/api/auth/api-keys"), {
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    apiKeys?: TeacherApiKeySummary[];
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `GET /api/auth/api-keys failed (${res.status})`);
  }
  if (!data.apiKeys) throw new Error("API keys response incomplete");
  return data as { apiKeys: TeacherApiKeySummary[] };
}

export async function postTeacherApiKey(
  bearer: string,
  body: { label: string }
): Promise<{
  plaintextKey: string;
  apiKey: TeacherApiKeySummary;
  warning?: string;
}> {
  const res = await fetch(url("/api/auth/api-keys"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    plaintextKey?: string;
    apiKey?: TeacherApiKeySummary;
    warning?: string;
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `POST /api/auth/api-keys failed (${res.status})`);
  }
  if (!data.plaintextKey || !data.apiKey) throw new Error("Create API key response incomplete");
  return data as {
    plaintextKey: string;
    apiKey: TeacherApiKeySummary;
    warning?: string;
  };
}

export async function deleteTeacherApiKey(bearer: string, keyId: string): Promise<{ revokedId: string }> {
  const res = await fetch(url(`/api/auth/api-keys/${encodeURIComponent(keyId)}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { revokedId?: string; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `DELETE /api/auth/api-keys failed (${res.status})`);
  }
  if (!data.revokedId) throw new Error("Revoke API key response incomplete");
  return data as { revokedId: string };
}

export interface StudentActiveCohort {
  cohortId: string;
  courseId: string;
  courseTitle: string;
  label: string;
  joinedAt: string;
  courseDescription?: string | null;
  heroImageKey?: string | null;
  heroImageUrl?: string | null;
  accentColor?: string | null;
  teacherEmail?: string | null;
}

export interface StudentCohortAssignmentReleased {
  assignmentId: string;
  itemKind?: "lesson" | "quiz";
  lessonId: string | null;
  quizId?: string | null;
  lessonTitle: string;
  itemTitle?: string;
  lessonDescription?: string | null;
  courseId: string;
  courseTitle: string;
  dueAt: string | null;
  allowGiveUp?: boolean;
  released: boolean;
  closed?: boolean;
  open: boolean;
  submissionAttemptCount?: number;
  latestOverallStatus?: string | null;
  progressStatus?: "not_started" | "in_progress" | "needs_help" | "complete";
  everRequiredPassed?: boolean;
  credited?: boolean;
  bestScorePercent?: number | null;
  attempt1Score?: number | null;
  attempt2Score?: number | null;
  retakeUsed?: boolean;
  retakeAvailable?: boolean;
  iconName?: string | null;
  iconBgColor?: string | null;
  iconFgColor?: string | null;
  workspaceMode?: LessonWorkspaceMode;
}

/** Lesson bundle for the student IDE (GET …/lesson-workspace). */
export interface StudentLessonWorkspaceTourStep {
  id: string;
  ordinal: number;
  title: string;
  body: string[];
  referenceCode: string | null;
}

export interface StudentLessonWorkspaceTest {
  id: string;
  lessonId: string;
  ordinal: number;
  name: string;
  isRequired: boolean;
  visibility: "hidden" | "summary" | "detailed";
  description: string | null;
  testCode: string;
}

export interface StudentLessonWorkspace {
  cohortId: string;
  lessonId: string;
  cohortAssignmentId: string;
  allowGiveUp: boolean;
  readOnly?: boolean;
  assignmentClosed?: boolean;
  assignmentOpen?: boolean;
  latestSubmission?: {
    attemptId: string;
    createdAt: string;
    requiredPassed: boolean;
    mainPy: string;
  } | null;
  /** Where preview.mainPy came from, which drives editor restore vs local draft. */
  editorBootstrap?: "starter" | "workspace" | "submission";
  progressStatus?: "not_started" | "in_progress" | "needs_help" | "complete";
  everRequiredPassed?: boolean;
  submissionAttemptCount?: number;
  lesson: { id: string; title: string; workspaceMode?: LessonWorkspaceMode };
  starterFiles?: { path: string; content: string }[];
  preview: { mainPy: string };
  tourSteps: StudentLessonWorkspaceTourStep[];
  lessonTests: StudentLessonWorkspaceTest[];
}

export async function getStudentLessonWorkspace(
  bearer: string,
  cohortId: string,
  lessonId: string
): Promise<StudentLessonWorkspace> {
  const res = await fetch(
    url(`/api/me/cohorts/${encodeURIComponent(cohortId)}/lessons/${encodeURIComponent(lessonId)}/lesson-workspace`),
    { headers: { Authorization: `Bearer ${bearer}` } }
  );
  const data = (await res.json().catch(() => ({}))) as StudentLessonWorkspace & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `GET lesson workspace failed (${res.status})`);
  }
  if (!data.lesson || !data.preview || !Array.isArray(data.tourSteps) || !Array.isArray(data.lessonTests)) {
    throw new Error("Lesson workspace response incomplete");
  }
  return data as StudentLessonWorkspace;
}

export async function postStudentLessonWorkspaceReset(
  bearer: string,
  cohortId: string,
  lessonId: string
): Promise<{
  cohortId: string;
  lessonId: string;
  editorBootstrap: "starter";
  preview: { mainPy: string };
  starterFiles: { path: string; content: string }[];
}> {
  const res = await fetch(
    url(
      `/api/me/cohorts/${encodeURIComponent(cohortId)}/lessons/${encodeURIComponent(lessonId)}/workspace-reset`
    ),
    {
      method: "POST",
      headers: { Authorization: `Bearer ${bearer}` },
    }
  );
  const data = (await res.json().catch(() => ({}))) as {
    cohortId?: string;
    lessonId?: string;
    editorBootstrap?: "starter";
    preview?: { mainPy?: string };
    starterFiles?: { path: string; content: string }[];
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `POST workspace reset failed (${res.status})`);
  }
  if (!data.preview || !Array.isArray(data.starterFiles)) {
    throw new Error("Workspace reset response incomplete");
  }
  return data as {
    cohortId: string;
    lessonId: string;
    editorBootstrap: "starter";
    preview: { mainPy: string };
    starterFiles: { path: string; content: string }[];
  };
}

export async function postStudentLessonSubmission(
  bearer: string,
  cohortId: string,
  lessonId: string,
  body: {
    workspaceFiles: unknown;
    validationEngine: string;
    overallStatus: string;
    requiredPassed: boolean;
    optionalPassed: boolean;
    testResults: unknown[];
    submissionKind?: "normal" | "gave_up";
  }
): Promise<{ submission: unknown }> {
  const res = await fetch(
    url(`/api/me/cohorts/${encodeURIComponent(cohortId)}/lessons/${encodeURIComponent(lessonId)}/submissions`),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearer}`,
      },
      body: JSON.stringify(body),
    }
  );
  const data = (await res.json().catch(() => ({}))) as { submission?: unknown; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `POST lesson submission failed (${res.status})`);
  }
  if (!data.submission) throw new Error("Lesson submission response incomplete");
  return data as { submission: unknown };
}

export async function getStudentMeCohorts(bearer: string): Promise<{ cohorts: StudentActiveCohort[] }> {
  const res = await fetch(url("/api/me/cohorts"), {
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { cohorts?: StudentActiveCohort[]; error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `GET /api/me/cohorts failed (${res.status})`);
  }
  if (!data.cohorts) throw new Error("Me cohorts response incomplete");
  return data as { cohorts: StudentActiveCohort[] };
}

export async function getStudentReleasedAssignments(
  bearer: string,
  cohortId: string
): Promise<{ cohortId: string; assignments: StudentCohortAssignmentReleased[] }> {
  const res = await fetch(
    url(`/api/me/cohorts/${encodeURIComponent(cohortId)}/assignments`),
    { headers: { Authorization: `Bearer ${bearer}` } }
  );
  const data = (await res.json().catch(() => ({}))) as {
    cohortId?: string;
    assignments?: StudentCohortAssignmentReleased[];
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `GET student assignments failed (${res.status})`);
  }
  if (typeof data.cohortId !== "string" || !Array.isArray(data.assignments)) {
    throw new Error("student assignments response incomplete");
  }
  return data as { cohortId: string; assignments: StudentCohortAssignmentReleased[] };
}

export interface StudentQuizQuestion {
  id: string;
  prompt: string;
  options: string[];
}

export interface StudentQuizReviewItem {
  questionId: string;
  prompt: string;
  options: string[];
  selectedOptionIndex: number;
  wasCorrect: boolean;
}

export interface StudentQuizWorkspace {
  quiz: {
    id: string;
    title: string;
    description: string | null;
    displayQuestionCount: number;
    iconName?: string | null;
    iconBgColor?: string | null;
    iconFgColor?: string | null;
  };
  assignmentId: string;
  released: boolean;
  closed: boolean;
  open: boolean;
  attemptCount: number;
  attempt1Score: number | null;
  attempt2Score: number | null;
  bestScorePercent: number | null;
  retakeUsed: boolean;
  retakeAvailable: boolean;
  progressStatus: "not_started" | "in_progress" | "complete";
  activeAttemptNumber: 1 | 2 | null;
  activeQuestions: StudentQuizQuestion[];
  review: StudentQuizReviewItem[] | null;
}

export async function getStudentQuizWorkspace(
  bearer: string,
  cohortId: string,
  quizId: string,
  opts?: { startRetake?: boolean }
): Promise<StudentQuizWorkspace> {
  const qs = opts?.startRetake ? "?startRetake=true" : "";
  const { res, data } = await getJsonWithTransientRetries<StudentQuizWorkspace & { error?: string }>(
    `/api/me/cohorts/${encodeURIComponent(cohortId)}/quizzes/${encodeURIComponent(quizId)}${qs}`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `GET quiz workspace failed (${res.status})`);
  if (!data?.quiz) throw new Error("Quiz workspace response incomplete");
  return data as StudentQuizWorkspace;
}

export async function postStudentQuizAttempt(
  bearer: string,
  cohortId: string,
  quizId: string,
  body: {
    attemptNumber: 1 | 2;
    responses: { questionId: string; selectedOptionIndex: number }[];
  }
): Promise<{
  attemptNumber: number;
  scorePercent: number;
  correctCount: number;
  totalCount: number;
  bestScorePercent: number;
  attempt1Score: number | null;
  attempt2Score: number | null;
  retakeUsed: boolean;
  retakeAvailable: boolean;
  review: StudentQuizReviewItem[];
}> {
  const res = await fetch(
    url(`/api/me/cohorts/${encodeURIComponent(cohortId)}/quizzes/${encodeURIComponent(quizId)}/attempts`),
    {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
      body: JSON.stringify(body),
    }
  );
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown> & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `POST quiz attempt failed (${res.status})`);
  return data as ReturnType<typeof postStudentQuizAttempt> extends Promise<infer T> ? T : never;
}

export async function postStudentCohortJoin(
  bearer: string,
  body: { joinCode: string }
): Promise<{
  cohortId: string;
  enrollmentId: string;
  joinedAt: string;
  studentUserId: string;
  status: string;
  accessToken?: string;
  tokenType?: string;
  user?: {
    googleSub: string;
    email: string;
    role: string;
    enrollmentSummary?: string;
  };
}> {
  const res = await fetch(url("/api/cohorts/join"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    cohortId?: string;
    enrollmentId?: string;
    joinedAt?: string;
    studentUserId?: string;
    status?: string;
    accessToken?: string;
    tokenType?: string;
    user?: {
      googleSub?: string;
      email?: string;
      role?: string;
      enrollmentSummary?: string;
    };
    error?: string;
  };
  if (!res.ok) {
    throw new Error(data.error ?? `Join cohort failed (${res.status})`);
  }
  if (!data.cohortId || !data.enrollmentId || !data.joinedAt || !data.studentUserId || !data.status) {
    throw new Error("Join cohort response incomplete");
  }
  return {
    cohortId: data.cohortId,
    enrollmentId: data.enrollmentId,
    joinedAt: data.joinedAt,
    studentUserId: data.studentUserId,
    status: data.status,
    accessToken: data.accessToken,
    tokenType: data.tokenType,
    user: data.user?.googleSub
      ? {
          googleSub: data.user.googleSub,
          email: data.user.email ?? "",
          role: data.user.role ?? "student",
          enrollmentSummary: data.user.enrollmentSummary,
        }
      : undefined,
  };
}

export interface PlaygroundProjectFile {
  path: string;
  content: string;
}

export interface PlaygroundProjectSummary {
  id: string;
  title: string;
  fileCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface PlaygroundProjectDetail {
  id: string;
  title: string;
  files: PlaygroundProjectFile[];
  createdAt: string;
  updatedAt: string;
}

export interface CohortPlaygroundProjectSummary extends PlaygroundProjectSummary {
  ownerUserId: string;
  ownerEmail: string;
}

export interface CohortPlaygroundProjectDetail extends PlaygroundProjectDetail {
  ownerUserId: string;
  ownerEmail: string;
}

export async function getPlaygroundProjects(
  bearer: string
): Promise<{ projects: PlaygroundProjectSummary[] }> {
  const { res, data } = await getJsonWithTransientRetries<{ projects?: PlaygroundProjectSummary[] }>(
    "/api/playground/projects",
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `GET playground projects failed (${res.status})`);
  if (!data?.projects) throw new Error("Playground projects response incomplete");
  return data as { projects: PlaygroundProjectSummary[] };
}

export async function postPlaygroundProject(
  bearer: string,
  body: { title: string; files?: PlaygroundProjectFile[] }
): Promise<{ project: PlaygroundProjectDetail }> {
  const res = await fetch(url("/api/playground/projects"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { project?: PlaygroundProjectDetail; error?: string };
  if (!res.ok) throw new Error(data.error ?? `Create playground project failed (${res.status})`);
  if (!data.project) throw new Error("Create playground project response incomplete");
  return data as { project: PlaygroundProjectDetail };
}

export async function getPlaygroundProject(
  bearer: string,
  projectId: string
): Promise<{ project: PlaygroundProjectDetail }> {
  const { res, data } = await getJsonWithTransientRetries<{ project?: PlaygroundProjectDetail }>(
    `/api/playground/projects/${encodeURIComponent(projectId)}`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `GET playground project failed (${res.status})`);
  if (!data?.project) throw new Error("Playground project response incomplete");
  return data as { project: PlaygroundProjectDetail };
}

export async function putPlaygroundProject(
  bearer: string,
  projectId: string,
  body: { title?: string; files?: PlaygroundProjectFile[] }
): Promise<{ project: PlaygroundProjectSummary }> {
  const res = await fetch(url(`/api/playground/projects/${encodeURIComponent(projectId)}`), {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { project?: PlaygroundProjectSummary; error?: string };
  if (!res.ok) throw new Error(data.error ?? `Save playground project failed (${res.status})`);
  if (!data.project) throw new Error("Save playground project response incomplete");
  return data as { project: PlaygroundProjectSummary };
}

export async function deletePlaygroundProject(
  bearer: string,
  projectId: string
): Promise<{ deleted: boolean }> {
  const res = await fetch(url(`/api/playground/projects/${encodeURIComponent(projectId)}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { deleted?: boolean; error?: string };
  if (!res.ok) throw new Error(data.error ?? `Delete playground project failed (${res.status})`);
  return { deleted: data.deleted === true };
}

export async function getCohortPlaygroundProjects(
  bearer: string,
  cohortId: string
): Promise<{ cohortId: string; projects: CohortPlaygroundProjectSummary[] }> {
  const { res, data } = await getJsonWithTransientRetries<{
    cohortId?: string;
    projects?: CohortPlaygroundProjectSummary[];
  }>(`/api/cohorts/${encodeURIComponent(cohortId)}/playground-projects`, bearer);
  if (!res.ok) throw new Error(data?.error ?? `GET cohort playground projects failed (${res.status})`);
  if (!data?.projects) throw new Error("Cohort playground projects response incomplete");
  return { cohortId, projects: data.projects };
}

export async function getCohortPlaygroundProject(
  bearer: string,
  cohortId: string,
  projectId: string
): Promise<{ cohortId: string; project: CohortPlaygroundProjectDetail; readOnly: boolean }> {
  const { res, data } = await getJsonWithTransientRetries<{
    cohortId?: string;
    project?: CohortPlaygroundProjectDetail;
    readOnly?: boolean;
  }>(
    `/api/cohorts/${encodeURIComponent(cohortId)}/playground-projects/${encodeURIComponent(projectId)}`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `GET cohort playground project failed (${res.status})`);
  if (!data?.project) throw new Error("Cohort playground project response incomplete");
  return { cohortId, project: data.project, readOnly: data.readOnly === true };
}

export async function patchUsersMe(
  bearer: string,
  body: { role?: "teacher" | "student"; enrollmentSummary?: LocalProfile["enrollmentSummary"] }
): Promise<SessionResponse> {
  const res = await fetch(url("/api/users/me"), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearer}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as SessionResponse & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `PATCH /api/users/me failed (${res.status})`);
  }
  if (!data.accessToken) {
    throw new Error("PATCH response missing accessToken");
  }
  return data as SessionResponse;
}

export interface AuthMePermissionsResponse {
  googleSub: string;
  email: string;
  role: string;
  authKind: string;
  apiKeyId: string | null;
  isAdmin: boolean;
}

export async function getAuthMePermissions(bearer: string): Promise<AuthMePermissionsResponse> {
  const res = await fetch(url("/api/auth/me/permissions"), {
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as AuthMePermissionsResponse & { error?: string };
  if (!res.ok) {
    throw new Error(data.error ?? `GET /api/auth/me/permissions failed (${res.status})`);
  }
  return data;
}

export interface CommunityLessonSummary {
  id: string;
  title: string;
  description: string | null;
  communityPublishedAt?: string | null;
  usedInCourseCount: number;
  version?: number;
  authorEmail?: string | null;
  authorName?: string | null;
}

export interface CommunityCourseSummary {
  id: string;
  title: string;
  description: string | null;
  communityPublishedAt?: string | null;
  usedInCohortCount: number;
  version?: number;
  authorEmail?: string | null;
  authorName?: string | null;
  heroImageKey?: string | null;
  heroImageUrl?: string | null;
  lessonCount?: number;
}

export type CommunitySearchHit = {
  kind: "lesson" | "course";
  id: string;
  title: string;
  description: string | null;
  score: number;
  authorEmail?: string | null;
  authorName?: string | null;
  heroImageKey?: string | null;
  heroImageUrl?: string | null;
  usedInCourseCount?: number;
  usedInCohortCount?: number;
};

export async function searchCommunityCatalog(
  bearer: string,
  params?: { q?: string; limit?: number }
): Promise<{ results: CommunitySearchHit[]; query: string }> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  const q = qs.toString();
  const { res, data } = await getJsonWithTransientRetries<{
    results?: CommunitySearchHit[];
    query?: string;
    error?: string;
  }>(`/api/community/search${q ? `?${q}` : ""}`, bearer);
  if (!res.ok) throw new Error(data?.error ?? `Community search failed (${res.status})`);
  return { results: data.results ?? [], query: data.query ?? params?.q ?? "" };
}

export async function getCommunityLessons(
  bearer: string,
  params?: { q?: string; limit?: number; offset?: number }
): Promise<{ lessons: CommunityLessonSummary[]; page: { limit: number; offset: number; total: number } }> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const q = qs.toString();
  const { res, data } = await getJsonWithTransientRetries<{
    lessons?: CommunityLessonSummary[];
    page?: { limit: number; offset: number; total: number };
    error?: string;
  }>(`/api/community/lessons${q ? `?${q}` : ""}`, bearer);
  if (!res.ok) throw new Error(data?.error ?? `GET community lessons failed (${res.status})`);
  if (!data.lessons || !data.page) throw new Error("Community lessons response incomplete");
  return data as { lessons: CommunityLessonSummary[]; page: { limit: number; offset: number; total: number } };
}

export async function getCommunityLessonDetail(
  bearer: string,
  lessonId: string
): Promise<{
  lesson: CommunityLessonSummary & {
    iconName?: string | null;
    iconBgColor?: string | null;
    iconFgColor?: string | null;
    workspaceMode?: LessonWorkspaceMode;
  };
  preview: { mainPy: string };
  tests: { id: string; name: string; testCode?: string; isRequired: boolean; visibility: string; description: string | null; ordinal: number }[];
  tourSteps: { id: string; ordinal: number; title: string; body: string[]; referenceCode?: string }[];
}> {
  const { res, data } = await getJsonWithTransientRetries<{
    lesson?: CommunityLessonSummary & {
      iconName?: string | null;
      iconBgColor?: string | null;
      iconFgColor?: string | null;
      workspaceMode?: LessonWorkspaceMode;
    };
    preview?: { mainPy?: string };
    tests?: { id: string; name: string; testCode?: string; isRequired: boolean; visibility: string; description: string | null; ordinal: number }[];
    tourSteps?: { id: string; ordinal: number; title: string; body: string[]; referenceCode?: string }[];
    error?: string;
  }>(`/api/community/lessons/${encodeURIComponent(lessonId)}`, bearer);
  if (!res.ok) throw new Error(data?.error ?? `GET community lesson failed (${res.status})`);
  if (!data.lesson || !data.preview) throw new Error("Community lesson response incomplete");
  return {
    lesson: data.lesson,
    preview: { mainPy: data.preview.mainPy ?? "" },
    tests: data.tests ?? [],
    tourSteps: data.tourSteps ?? [],
  };
}

export async function getCommunityCourses(
  bearer: string,
  params?: { q?: string; limit?: number; offset?: number }
): Promise<{ courses: CommunityCourseSummary[]; page: { limit: number; offset: number; total: number } }> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const q = qs.toString();
  const { res, data } = await getJsonWithTransientRetries<{
    courses?: CommunityCourseSummary[];
    page?: { limit: number; offset: number; total: number };
    error?: string;
  }>(`/api/community/courses${q ? `?${q}` : ""}`, bearer);
  if (!res.ok) throw new Error(data?.error ?? `GET community courses failed (${res.status})`);
  if (!data.courses || !data.page) throw new Error("Community courses response incomplete");
  return data as { courses: CommunityCourseSummary[]; page: { limit: number; offset: number; total: number } };
}

export async function postUseCommunityCourse(
  bearer: string,
  courseId: string
): Promise<{ courseId: string; lessonIds: string[] }> {
  const res = await fetch(url(`/api/community/courses/${encodeURIComponent(courseId)}/use`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    courseId?: string;
    lessonIds?: string[];
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Use community course failed (${res.status})`);
  if (!data.courseId) throw new Error("Use community course response incomplete");
  return { courseId: data.courseId, lessonIds: data.lessonIds ?? [] };
}

export async function postUseCommunityLesson(
  bearer: string,
  lessonId: string
): Promise<{ lessonId: string }> {
  const res = await fetch(url(`/api/community/lessons/${encodeURIComponent(lessonId)}/use`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { lessonId?: string; error?: string };
  if (!res.ok) throw new Error(data.error ?? `Use community lesson failed (${res.status})`);
  if (!data.lessonId) throw new Error("Use community lesson response incomplete");
  return { lessonId: data.lessonId };
}

export interface InspirationInfo {
  sourceId: string;
  sourceTitle: string;
  authorEmail: string | null;
  authorName: string | null;
  sourceVersion: number | null;
  communityVersion: number | null;
  detached: boolean;
  updateAvailable: boolean;
  changelog: { version: number; changelog: string | null; publishedAt: string | null }[];
}

export async function getLessonInspiration(
  bearer: string,
  lessonId: string
): Promise<InspirationInfo | null> {
  const { res, data } = await getJsonWithTransientRetries<{ inspiration?: InspirationInfo; error?: string }>(
    `/api/lessons/${encodeURIComponent(lessonId)}/inspiration`,
    bearer
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(data?.error ?? `Inspiration lookup failed (${res.status})`);
  return data.inspiration ?? null;
}

export async function postUpgradeLesson(
  bearer: string,
  lessonId: string
): Promise<{ lessonId: string; version: number | null }> {
  const res = await fetch(url(`/api/lessons/${encodeURIComponent(lessonId)}/upgrade`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { lessonId?: string; version?: number; error?: string };
  if (!res.ok) throw new Error(data.error ?? `Upgrade lesson failed (${res.status})`);
  return { lessonId: data.lessonId ?? lessonId, version: data.version ?? null };
}

export async function postUpgradeAllLessons(
  bearer: string
): Promise<{ upgraded: { lessonId: string; title: string; toVersion: number }[] }> {
  const res = await fetch(url(`/api/lessons/upgrade-all`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    upgraded?: { lessonId: string; title: string; toVersion: number }[];
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Upgrade all lessons failed (${res.status})`);
  return { upgraded: data.upgraded ?? [] };
}

export async function getCourseInspiration(
  bearer: string,
  courseId: string
): Promise<InspirationInfo | null> {
  const { res, data } = await getJsonWithTransientRetries<{ inspiration?: InspirationInfo; error?: string }>(
    `/api/courses/${encodeURIComponent(courseId)}/inspiration`,
    bearer
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(data?.error ?? `Inspiration lookup failed (${res.status})`);
  return data.inspiration ?? null;
}

export async function postUpgradeCourse(
  bearer: string,
  courseId: string
): Promise<{ courseId: string; version: number | null; lessonIds: string[] }> {
  const res = await fetch(url(`/api/courses/${encodeURIComponent(courseId)}/upgrade`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    courseId?: string;
    version?: number;
    lessonIds?: string[];
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Upgrade course failed (${res.status})`);
  return { courseId: data.courseId ?? courseId, version: data.version ?? null, lessonIds: data.lessonIds ?? [] };
}

export async function postUpgradeAllCourses(
  bearer: string
): Promise<{ upgraded: { courseId: string; title: string; toVersion: number }[] }> {
  const res = await fetch(url(`/api/courses/upgrade-all`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    upgraded?: { courseId: string; title: string; toVersion: number }[];
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Upgrade all courses failed (${res.status})`);
  return { upgraded: data.upgraded ?? [] };
}

export async function postCourseHeroImageUploadUrl(
  bearer: string,
  courseId: string,
  body: { contentType: string; sizeBytes: number }
): Promise<{ uploadUrl: string; key: string; maxBytes: number; previewUrl?: string }> {
  const res = await fetch(url(`/api/courses/${encodeURIComponent(courseId)}/hero-image`), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    uploadUrl?: string;
    key?: string;
    maxBytes?: number;
    previewUrl?: string;
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Hero image upload URL failed (${res.status})`);
  if (!data.uploadUrl || !data.key) throw new Error("Hero image upload response incomplete");
  return {
    uploadUrl: data.uploadUrl,
    key: data.key,
    maxBytes: data.maxBytes ?? 0,
    ...(data.previewUrl ? { previewUrl: data.previewUrl } : {}),
  };
}

export async function postSuggestChangelog(
  bearer: string,
  scope: "lessons" | "courses",
  body: { before?: unknown; after: unknown }
): Promise<{ changelog: string }> {
  const res = await fetch(url(`/api/${scope}/ai/changelog-suggest`), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { changelog?: string; error?: string };
  if (!res.ok) throw new Error(data.error ?? `Changelog suggestion failed (${res.status})`);
  return { changelog: data.changelog ?? "" };
}

export async function postSubmitLessonToCommunity(
  bearer: string,
  lessonId: string
): Promise<{ lessonId: string; communityPublishStatus: CommunityPublishStatus }> {
  const res = await fetch(url(`/api/lessons/${encodeURIComponent(lessonId)}/community/submit`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    lessonId?: string;
    communityPublishStatus?: CommunityPublishStatus;
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Submit lesson failed (${res.status})`);
  return {
    lessonId: data.lessonId ?? lessonId,
    communityPublishStatus: data.communityPublishStatus ?? "pending_review",
  };
}

export async function postSubmitCourseToCommunity(
  bearer: string,
  courseId: string
): Promise<{ courseId: string; communityPublishStatus: CommunityPublishStatus }> {
  const res = await fetch(url(`/api/courses/${encodeURIComponent(courseId)}/community/submit`), {
    method: "POST",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as {
    courseId?: string;
    communityPublishStatus?: CommunityPublishStatus;
    error?: string;
  };
  if (!res.ok) throw new Error(data.error ?? `Submit course failed (${res.status})`);
  return {
    courseId: data.courseId ?? courseId,
    communityPublishStatus: data.communityPublishStatus ?? "pending_review",
  };
}

export interface CommunitySubmissionRow {
  kind: "lesson" | "course";
  id: string;
  title: string;
  description: string | null;
  submittedAt: string | null;
  ownerEmail: string;
}

export async function getAdminCommunitySubmissions(
  bearer: string
): Promise<{ submissions: CommunitySubmissionRow[] }> {
  const { res, data } = await getJsonWithTransientRetries<{
    submissions?: CommunitySubmissionRow[];
    error?: string;
  }>("/api/admin/community/submissions", bearer);
  if (!res.ok) throw new Error(data?.error ?? `Admin submissions failed (${res.status})`);
  return { submissions: data.submissions ?? [] };
}

export async function postAdminCommunityLessonAction(
  bearer: string,
  lessonId: string,
  action: "approve" | "reject",
  body?: { reason?: string }
): Promise<void> {
  const res = await fetch(
    url(`/api/admin/community/lessons/${encodeURIComponent(lessonId)}/${action}`),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${bearer}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body ?? {}),
    }
  );
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Admin lesson ${action} failed (${res.status})`);
}

export async function postAdminCommunityCourseAction(
  bearer: string,
  courseId: string,
  action: "approve" | "reject",
  body?: { reason?: string }
): Promise<void> {
  const res = await fetch(
    url(`/api/admin/community/courses/${encodeURIComponent(courseId)}/${action}`),
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${bearer}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body ?? {}),
    }
  );
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Admin course ${action} failed (${res.status})`);
}

// ---------------------------------------------------------------------------
// Standards & glossary
// ---------------------------------------------------------------------------

export interface StandardSetSummary {
  id: string;
  name: string;
  description: string | null;
  jurisdiction: string | null;
  gradeBand: string | null;
  status: "draft" | "published";
  currentVersion: number;
  standardsCount: number;
  glossaryCount: number;
  latestDocumentStatus: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface StandardDraftItem {
  code: string;
  description: string;
  category?: string | null;
  gradeBand?: string | null;
  parentCode?: string | null;
}

export interface GlossaryDraftItem {
  term: string;
  definition: string;
}

export interface StandardsExtraction {
  standards: StandardDraftItem[];
  glossary: GlossaryDraftItem[];
}

export interface StandardRecord {
  id: string;
  code: string;
  description: string;
  category: string | null;
  gradeBand: string | null;
  parentCode: string | null;
  position: number;
}

export interface GlossaryTermRecord {
  id: string;
  term: string;
  definition: string;
  position: number;
}

export interface StandardSetDocumentInfo {
  id: string;
  version: number;
  status: "extracting" | "ready" | "failed";
  error: string | null;
}

export interface StandardSetDetail {
  standardSet: StandardSetSummary;
  document: StandardSetDocumentInfo | null;
  draft: StandardsExtraction | null;
  published: { standards: StandardRecord[]; glossary: GlossaryTermRecord[] };
}

export async function getAdminStandardSets(bearer: string): Promise<{ standardSets: StandardSetSummary[] }> {
  const { res, data } = await getJsonWithTransientRetries<{ standardSets?: StandardSetSummary[]; error?: string }>(
    "/api/admin/standard-sets",
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `Standard sets failed (${res.status})`);
  return { standardSets: data.standardSets ?? [] };
}

export async function postAdminStandardSet(
  bearer: string,
  body: { name: string; description?: string; jurisdiction?: string; gradeBand?: string }
): Promise<{ standardSet: StandardSetSummary }> {
  const res = await fetch(url("/api/admin/standard-sets"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { standardSet?: StandardSetSummary; error?: string };
  if (!res.ok || !data.standardSet) throw new Error(data.error ?? `Create standard set failed (${res.status})`);
  return { standardSet: data.standardSet };
}

export async function getAdminStandardSet(bearer: string, setId: string): Promise<StandardSetDetail> {
  const { res, data } = await getJsonWithTransientRetries<StandardSetDetail & { error?: string }>(
    `/api/admin/standard-sets/${encodeURIComponent(setId)}`,
    bearer
  );
  if (!res.ok || !data.standardSet) throw new Error(data?.error ?? `Standard set failed (${res.status})`);
  return data;
}

export async function patchAdminStandardSet(
  bearer: string,
  setId: string,
  body: { name?: string; description?: string | null; jurisdiction?: string | null; gradeBand?: string | null }
): Promise<{ standardSet: StandardSetSummary }> {
  const res = await fetch(url(`/api/admin/standard-sets/${encodeURIComponent(setId)}`), {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { standardSet?: StandardSetSummary; error?: string };
  if (!res.ok || !data.standardSet) throw new Error(data.error ?? `Update standard set failed (${res.status})`);
  return { standardSet: data.standardSet };
}

export async function deleteAdminStandardSet(bearer: string, setId: string): Promise<void> {
  const res = await fetch(url(`/api/admin/standard-sets/${encodeURIComponent(setId)}`), {
    method: "DELETE",
    headers: { Authorization: `Bearer ${bearer}` },
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Delete standard set failed (${res.status})`);
}

export async function postAdminStandardSetUploadUrl(
  bearer: string,
  setId: string,
  body: { contentType: string; sizeBytes: number }
): Promise<{ uploadUrl: string; key: string; maxBytes: number }> {
  const res = await fetch(url(`/api/admin/standard-sets/${encodeURIComponent(setId)}/document-upload-url`), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    uploadUrl?: string;
    key?: string;
    maxBytes?: number;
    error?: string;
  };
  if (!res.ok || !data.uploadUrl || !data.key) throw new Error(data.error ?? `Upload URL failed (${res.status})`);
  return { uploadUrl: data.uploadUrl, key: data.key, maxBytes: data.maxBytes ?? 0 };
}

export async function postAdminStandardSetExtract(
  bearer: string,
  setId: string,
  body: { pdfKey: string }
): Promise<{ jobId: string; documentVersion: number }> {
  const res = await fetch(url(`/api/admin/standard-sets/${encodeURIComponent(setId)}/extract`), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { jobId?: string; documentVersion?: number; error?: string };
  if (!res.ok || !data.jobId) throw new Error(data.error ?? `Extract failed (${res.status})`);
  return { jobId: data.jobId, documentVersion: data.documentVersion ?? 0 };
}

export async function pollAdminStandardsJob(bearer: string, jobId: string): Promise<void> {
  const maxAttempts = 120;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const res = await fetch(url(`/api/admin/standards-jobs/${encodeURIComponent(jobId)}`), {
      headers: { Authorization: `Bearer ${bearer}` },
    });
    const data = (await res.json().catch(() => ({}))) as { status?: string; error?: string };
    if (!res.ok) throw new ApiClientError(data.error ?? `Job poll failed (${res.status})`, { status: res.status });
    if (data.status === "done") return;
    if (data.status === "failed") throw new ApiClientError(data.error ?? "Extraction failed", { status: 500 });
    await sleep(attempt < 6 ? 2_000 : 3_500);
  }
  throw new Error("Extraction is taking longer than expected; refresh to check status.");
}

export async function putAdminStandardSetDraft(
  bearer: string,
  setId: string,
  draft: StandardsExtraction
): Promise<{ draft: StandardsExtraction }> {
  const res = await fetch(url(`/api/admin/standard-sets/${encodeURIComponent(setId)}/draft`), {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(draft),
  });
  const data = (await res.json().catch(() => ({}))) as { draft?: StandardsExtraction; error?: string };
  if (!res.ok || !data.draft) throw new Error(data.error ?? `Save draft failed (${res.status})`);
  return { draft: data.draft };
}

export async function postAdminStandardSetPublish(
  bearer: string,
  setId: string,
  draft?: StandardsExtraction
): Promise<{ standardSet: StandardSetSummary }> {
  const res = await fetch(url(`/api/admin/standard-sets/${encodeURIComponent(setId)}/publish`), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify(draft ?? {}),
  });
  const data = (await res.json().catch(() => ({}))) as { standardSet?: StandardSetSummary; error?: string };
  if (!res.ok || !data.standardSet) throw new Error(data.error ?? `Publish failed (${res.status})`);
  return { standardSet: data.standardSet };
}

// Teacher-facing reads (published only)

export async function getStandardSets(bearer: string): Promise<{ standardSets: StandardSetSummary[] }> {
  const { res, data } = await getJsonWithTransientRetries<{ standardSets?: StandardSetSummary[]; error?: string }>(
    "/api/standard-sets",
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `Standard sets failed (${res.status})`);
  return { standardSets: data.standardSets ?? [] };
}

export async function getStandardsForSet(bearer: string, setId: string): Promise<{ standards: StandardRecord[] }> {
  const { res, data } = await getJsonWithTransientRetries<{ standards?: StandardRecord[]; error?: string }>(
    `/api/standard-sets/${encodeURIComponent(setId)}/standards`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `Standards failed (${res.status})`);
  return { standards: data.standards ?? [] };
}

export async function getGlossaryForSet(bearer: string, setId: string): Promise<{ glossary: GlossaryTermRecord[] }> {
  const { res, data } = await getJsonWithTransientRetries<{ glossary?: GlossaryTermRecord[]; error?: string }>(
    `/api/standard-sets/${encodeURIComponent(setId)}/glossary`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `Glossary failed (${res.status})`);
  return { glossary: data.glossary ?? [] };
}

export async function getStandardSetSourceDocument(
  bearer: string,
  setId: string
): Promise<{ viewUrl: string }> {
  const { res, data } = await getJsonWithTransientRetries<{ viewUrl?: string; error?: string }>(
    `/api/standard-sets/${encodeURIComponent(setId)}/source-document`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `Source document failed (${res.status})`);
  if (!data.viewUrl) throw new Error("Source document URL missing");
  return { viewUrl: data.viewUrl };
}

export async function getLessonStandards(
  bearer: string,
  lessonId: string
): Promise<{ standards: LessonStandardLink[] }> {
  const { res, data } = await getJsonWithTransientRetries<{ standards?: LessonStandardLink[]; error?: string }>(
    `/api/lessons/${encodeURIComponent(lessonId)}/standards`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `Lesson standards failed (${res.status})`);
  return { standards: data.standards ?? [] };
}

export async function getLessonGlossary(
  bearer: string,
  lessonId: string
): Promise<{ glossary: LessonGlossaryLink[] }> {
  const { res, data } = await getJsonWithTransientRetries<{ glossary?: LessonGlossaryLink[]; error?: string }>(
    `/api/lessons/${encodeURIComponent(lessonId)}/glossary`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `Lesson glossary failed (${res.status})`);
  return { glossary: data.glossary ?? [] };
}

export async function putLessonStandards(
  bearer: string,
  lessonId: string,
  standardIds: string[]
): Promise<void> {
  const res = await fetch(url(`/api/lessons/${encodeURIComponent(lessonId)}/standards`), {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify({ standardIds }),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Save lesson standards failed (${res.status})`);
}

export async function putLessonGlossary(
  bearer: string,
  lessonId: string,
  glossaryTermIds: string[]
): Promise<void> {
  const res = await fetch(url(`/api/lessons/${encodeURIComponent(lessonId)}/glossary`), {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify({ glossaryTermIds }),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Save lesson glossary failed (${res.status})`);
}

export interface StandardCoverageLesson {
  id: string;
  title: string;
}

export interface CourseStandardsCoverage {
  standardSetId: string | null;
  standardSets: StandardSetSummary[];
  covered: { standard: StandardRecord; lessons: StandardCoverageLesson[] }[];
  uncovered: StandardRecord[];
  coverageRatio: number;
  glossaryCovered: { term: GlossaryTermRecord; lessons: StandardCoverageLesson[] }[];
  glossaryUncovered: GlossaryTermRecord[];
  glossaryCoverageRatio: number;
  hasSourceDocument: boolean;
}

export async function getCourseStandardSets(
  bearer: string,
  courseId: string
): Promise<{ standardSets: StandardSetSummary[] }> {
  const { res, data } = await getJsonWithTransientRetries<{ standardSets?: StandardSetSummary[]; error?: string }>(
    `/api/courses/${encodeURIComponent(courseId)}/standard-sets`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `Course standard sets failed (${res.status})`);
  return { standardSets: data.standardSets ?? [] };
}

export async function putCourseStandardSets(
  bearer: string,
  courseId: string,
  standardSetIds: string[]
): Promise<void> {
  const res = await fetch(url(`/api/courses/${encodeURIComponent(courseId)}/standard-sets`), {
    method: "PUT",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${bearer}` },
    body: JSON.stringify({ standardSetIds }),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Save course standard sets failed (${res.status})`);
}

export async function getCourseStandardsCoverage(
  bearer: string,
  courseId: string,
  setId?: string
): Promise<CourseStandardsCoverage> {
  const qs = setId ? `?setId=${encodeURIComponent(setId)}` : "";
  const { res, data } = await getJsonWithTransientRetries<CourseStandardsCoverage & { error?: string }>(
    `/api/courses/${encodeURIComponent(courseId)}/standards-coverage${qs}`,
    bearer
  );
  if (!res.ok) throw new Error(data?.error ?? `Coverage failed (${res.status})`);
  return data;
}
